/*
 * ProjectDashboard (Phase 4 — Reimplementierung)
 *
 * Komplett neu gegen die Phase-1-Primitive (`drawMenuFrame`, `drawMenuItem`,
 * `addTextSpan`). Englische Strings, 5-spaltige Liste, persistente Selektion
 * mit kontextabhaengiger Action-Bar.
 *
 * Stufe B: Layout-Skelett, flache Liste, Sort, Toggle Projects/Trash, alle
 * Aktionen funktional (ausser Folder).
 *
 * Stufe C (aktuell): Folder-Hierarchie. Liste = Folder + Projekte des
 * aktuellen Folders, Parent-Eintrag oben (ausser Root), dynamischer
 * Breadcrumb, Folder-Werte (Size leer, Modified = juengstes updatedAt im
 * Unterbaum, Created = folder.createdAt, Cloud leer). Open auf Folder /
 * Parent navigiert; Cmd+Up + Breadcrumb-Klick + lastFolder folgen in C-2,
 * Inline-Rename + New Folder in C-3, Folder-Delete-All in C-4.
 *
 * PathPicker, Drag&Drop, Suche und Context-Menue folgen in Stufen D–G.
 *
 * Spec: docs/project-dashboard-specification.md
 */

import { Style, MINIMAP } from '../config.js'
import { computeStringLayout } from '../GlyphMetrics.js'
import { formatRelativeDate, formatTrashExpiry, formatSize } from './dateFormat.js'
import { SingleLineInput } from '../SingleLineInput.js'
import { PathPicker } from './PathPicker.js'
import { blurKeyboardSink } from '../touchKeyboard.js'
import { enableCloudForNewProject } from './cloudToggleLegacy.js'

// ─── Konstanten ────────────────────────────────────────────────────

// Spaltenbreiten (Spec §3.1). Name nimmt den Rest.
const COL_GUTTER  = 2
const COL_SIZE    = 8
const COL_DATE    = 12   // Modified / Created (gleiche Breite)
const COL_CLOUD   = 5    // "Cloud" oder leer
const COL_GAP     = 2    // Abstand zwischen Spalten
const COL_NAME_MIN = 30
const SCROLLBAR_W = 1    // reservierte Spalte rechts

// Vertikale Reserven (Spec §1.1: 3 Zeilen Luft oben+unten)
const FRAME_PAD_TOP = 3
const FRAME_PAD_BOTTOM = 3
// Interne Zeilen: Title(BorderTop, von drawMenuFrame), Breadcrumb(1), Sep(1),
// ColHeader(1), Sep(1), Liste(N), Sep(1), ActionBar(1..2), BorderBottom.
const INNER_HEADER_LINES = 4  // Breadcrumb + sep + colHeader + sep
const INNER_FOOTER_LINES = 2  // sep + ActionBar (1 Zeile, kann auf 2 wachsen)

// localStorage-Keys
const LS_SORT = 'dashboard.sort'
const LS_LAST_FOLDER = 'dashboard.lastFolder'

// Search-Box (Stufe F). Fixe Breite — passt in die Breadcrumb-Zeile rechts;
// Breadcrumb wird bei Bedarf abgeschnitten. Inkl. Mini-Frame [ … ] = 2
// Brackets + inneres Padding.
const SEARCH_BOX_WIDTH = 28

// Sortierbare Spalten (Cloud-Spalte ist NICHT sortierbar — Spec §3.1)
const SORT_FIELDS = ['name', 'size', 'updated', 'created']

/**
 * Mischt zwei `#rrggbb`-Farben in Verhaeltnis `t` (0..1) — t=1 → c1, t=0 → c2.
 * Pseudo-Alpha-Helper fuer den Scrollbar (Boris-Wunsch: keine globalAlpha,
 * stattdessen opake Mischfarben).
 * @param {string} c1
 * @param {string} c2
 * @param {number} t
 * @returns {string}
 */
function _mixColors(c1, c2, t) {
    const parse = (s) => {
        if (typeof s !== 'string') return [0, 0, 0]
        const m = s.replace('#', '')
        if (m.length < 6) return [0, 0, 0]
        return [
            parseInt(m.slice(0, 2), 16),
            parseInt(m.slice(2, 4), 16),
            parseInt(m.slice(4, 6), 16)
        ]
    }
    const [r1, g1, b1] = parse(c1)
    const [r2, g2, b2] = parse(c2)
    const mix = (a, b) => Math.round(a * t + b * (1 - t))
    const hex = (n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')
    return `#${hex(mix(r1, r2))}${hex(mix(g1, g2))}${hex(mix(b1, b2))}`
}

// Action-Buchstaben (Spec §5.1). Achtung: Konflikte vermieden, [X] fuer Delete.
const ACTIONS = {
    OPEN: 'O', CLOSE_TAB: 'W', RENAME: 'R', MOVE: 'M', DUPLICATE: 'D',
    DELETE: 'X', CLOUD: 'C', NEW_PROJECT: 'N', NEW_FOLDER: 'F',
    RESTORE: 'R', PERM_DELETE: 'P', EMPTY_TRASH: 'E'
}

export class ProjectDashboard {
    /**
     * @param {Object} deps
     *   @param {import('../overlay.js').Overlay}                    deps.overlay
     *   @param {import('../persistence/ProjectManager.js').ProjectManager} deps.projectManager
     *   @param {import('../TabManager.js').TabManager}              deps.tabManager
     *   @param {import('../DialogManager.js').DialogManager}        deps.dialogManager
     *   @param {import('../persistence/FolderManager.js').FolderManager} [deps.folderManager]
     *   @param {import('../SoundManager.js').SoundManager}          [deps.soundManager]
     */
    constructor({ overlay, overlayTop, projectManager, tabManager, dialogManager, folderManager, soundManager, renderer, cursor }) {
        this._overlay = overlay
        // overlayTop = Top-Layer (Modal-Stacking). Dashboard rendert auf
        // overlay (Base-Layer); der PathPicker (und in Stufe G das
        // Context-Menu) rendert hier drauf, damit der Renderer ihn nach
        // dem Dashboard zeichnet und kein Cell/Stripe/Span darunter
        // durchscheint. Falls nicht injiziert (z.B. in Tests), faellt
        // Picker-Rendering auf das Base-Overlay zurueck — funktional, aber
        // optisch nicht ideal.
        this._overlayTop = overlayTop || null
        this._projectManager = projectManager
        this._tabManager = tabManager
        this._dialogManager = dialogManager
        this._folderManager = folderManager || null
        this._soundManager = soundManager || null
        // Renderer-Reference fuer Mode-/Pixel-Math beim Scrollbar (Editor-
        // Pattern braucht Inset + Edge-Streifen in Pixel-Geometrie).
        this._renderer = renderer || null
        // Globaler Cursor-State fuer das Blinken des Inline-Edit-Cursors.
        // Wenn nicht injiziert, faellt _renderEditRow auf "immer sichtbar"
        // zurueck (kein Blinken, aber funktional).
        this._cursor = cursor || null
        this._syncService = null

        // State
        this.isOpen = false
        this._projects = []          // ALLE aktiven Projekte (Cache)
        this._folders = []           // ALLE aktiven Folder (Cache)
        this._trashProjects = []
        // Aktueller Folder (null = Root "My Projects"). Stufe C.
        this._currentFolderId = null
        this._currentFolderPath = []  // Array von Folder-Objekten (Root→…→aktuell)
        // Entries der aktuell sichtbaren Liste (kombiniert Folders + Projects
        // des aktuellen Folders, plus Parent-Eintrag oben — siehe _rebuildEntries).
        // Item-Schema: { type: 'parent' | 'folder' | 'project', folder?, project? }
        this._entries = []
        this._selectedIndex = 0
        this._hoveredIndex = -1
        this._hoveredButtonKey = null  // Action-Bar Hover (Button-Key oder null)
        this._showTrash = false
        this._scrollOffset = 0
        this._wheelAccumulator = 0
        // Standard-Sort: zuletzt geaendert oben (Spec §3.3)
        this._sortBy = 'updated'
        this._sortOrder = 'desc'
        this._loadSortFromStorage()

        // Layout-Cache (fuer Hit-Tests)
        this._layout = null

        // Open-Callback (wird beim Selektieren+Open getriggert)
        this._onOpen = null

        // Inline-Edit-State (Stufe C-3). null = kein Edit aktiv.
        // mode: 'newFolder' | 'renameFolder' | 'renameProject'
        // input: SingleLineInput-Instanz
        // target: betroffene Folder/Project-UUID (null bei newFolder)
        this._editState = null

        // PathPicker (Stufe D). Eigene Klasse, vom Dashboard als Modal-Layer
        // ueber sich selbst gerendert. Alle Eingaben werden vom Dashboard zuerst
        // an den Picker delegiert.
        this._pathPicker = new PathPicker()

        // Kein-Projekt-Zustand (Stufe J, Spec §1.4 / §11.5). _hintPulseUntil
        // ist die Timestamp (Date.now() + 200) bis zu der die Header-Hint-
        // Zeile als Pulse-Variante gerendert wird. ESC und Klick-Ausserhalb
        // sind in diesem Zustand blockiert — beim Versuch wird der Pulse
        // gestartet und ein Ping abgespielt.
        this._hintPulseUntil = 0
        this._hintPulseTimer = null

        // Context-Menu (Stufe G). null = nicht offen; sonst:
        //   { items, anchorX, anchorY, x, y, width, height, hoveredIndex,
        //     selectedIndex, targetEntry, targetIsEmpty }
        // items = [{ key, label, action }] — analog Action-Bar, aber nur
        // erlaubte Items werden gezeigt (Spec §5.5). Position folgt der
        // Maus, mit Auto-Snap am unteren/rechten Frame-Rand.
        this._contextMenu = null

        // Suche (Stufe F). _searchActive bedeutet "Fokus liegt in der
        // Search-Box, Tastatur geht dorthin"; _searchText der aktuelle
        // Filterstring. Solange _searchText.length > 0, ersetzt die globale
        // Treffer-Liste die Folder-Sicht — _currentList liefert dann
        // _searchResults. Ohne Treffer-Selektion ist _searchSelectedIndex
        // = -1 (alle Action-Buttons disabled, Spec §4.1 + §8.2).
        this._searchActive = false
        this._searchText = ''
        this._searchResults = []          // [{ project, segments }]
        this._searchSelectedIndex = -1
        this._searchHoveredIndex = -1
        // Eigener Scroll-Offset fuer die Treffer-Liste — getrennt vom
        // Folder-Modus-Scroll, weil Treffer 2 Cell-Zeilen pro Item sind.
        this._searchScrollOffset = 0
        this._searchWheelAccumulator = 0
        // Layout-Cache fuer Hit-Tests (gesetzt in _renderSearchBox).
        this._layoutSearch = null

        // Drag&Drop (Stufe E). Drei Phasen:
        //   1) _dragCandidate: mousedown auf einem Folder/Project — noch
        //      kein Drag, nur Pre-Drag (wird beim mouseup ohne Bewegung
        //      verworfen, dann lebt der nachfolgende Klick wie sonst).
        //   2) _dragState: erst gesetzt, wenn der Cursor seit mousedown
        //      ueber eine Cell-Grenze gewandert ist — ab hier wird die
        //      Ghost-Zeile gerendert und der Drop-Target getrackt.
        //   3) _justFinishedDrag: kurz nach erfolgreichem oder gecanceltem
        //      Drop true — verschluckt den vom Browser nachgereichten
        //      click-Event, der sonst die Drop-Selektion verschoebe.
        this._dragCandidate = null
        this._dragState = null
        this._justFinishedDrag = false
    }

    // ───────────────────────────────────────────────────────────────
    // Public API
    // ───────────────────────────────────────────────────────────────

    setSyncService(syncService) { this._syncService = syncService }
    setOnOpen(fn) { this._onOpen = fn }

    /**
     * True wenn ein Inline-Edit (New Folder / Rename) gerade aktiv ist.
     * Wird intern fuer Action-Bar-Disable-Logik verwendet.
     */
    hasActiveEdit() { return !!this._editState }

    /**
     * True wenn ein Dashboard-internes UI-Element einen blinkenden Cursor
     * traegt — entweder Inline-Edit (SingleLineInput) oder PathPicker-Filter.
     * main.js pollt das im Blink-Loop, damit der Cursor beim Toggle nicht
     * auf seinem letzten Zustand einfriert.
     */
    hasBlinkingCursor() {
        return !!this._editState
            || (this._pathPicker && this._pathPicker.isActive)
            || this._searchActive
    }

    /**
     * Wird vom Blink-Loop gerufen, wenn der globale Cursor toggelt. Triggert
     * einen Dashboard-Re-Render, sonst friert der Edit- bzw. Picker-Cursor
     * auf seinem letzten gezeichneten Zustand ein.
     */
    tickCursor() {
        if (!this.isOpen) return
        if (!this.hasBlinkingCursor()) return
        this._render()
    }

    /**
     * Aktiviert den Such-Modus (Fokus in der Search-Box). Text bleibt
     * erhalten. Selektion in der Treffer-Liste startet bei -1 (nichts
     * selektiert, Spec §8.2) — die Pfeiltasten setzen sie auf 0.
     */
    _activateSearch() {
        if (this._showTrash) return    // Spec §9: kein Search im Trash
        if (this._searchActive) return
        this._searchActive = true
        this._searchSelectedIndex = -1
        this._render()
    }

    /**
     * Deaktiviert den Such-Modus (Fokus weg von der Search-Box). Text
     * bleibt erhalten, damit der User per Klick auf einen Treffer oder
     * spaeteres Re-Aktivieren weiterarbeiten kann.
     */
    _deactivateSearch() {
        if (!this._searchActive) return
        this._searchActive = false
        this._render()
    }

    /**
     * Leert die Suche (Text + Results) und deaktiviert die Box. Wird beim
     * Treffer-Klick / -Enter sowie bei ESC mit Text gerufen.
     */
    _clearSearch() {
        this._searchActive = false
        this._searchText = ''
        this._searchResults = []
        this._searchSelectedIndex = -1
        this._searchHoveredIndex = -1
    }

    /**
     * Tastatur-Handler fuer das Context-Menu (Stufe G).
     */
    async _handleContextMenuKey(e) {
        const cm = this._contextMenu
        if (!cm) return
        switch (e.key) {
            case 'Escape':
                this._closeContextMenu()
                return
            case 'ArrowDown':
                cm.hoveredIndex = Math.min(cm.hoveredIndex + 1, cm.items.length - 1)
                this._render()
                return
            case 'ArrowUp':
                cm.hoveredIndex = Math.max(cm.hoveredIndex - 1, 0)
                this._render()
                return
            case 'Enter': {
                const item = cm.items[cm.hoveredIndex]
                if (item) await this._invokeContextMenuItem(item)
                return
            }
            default: {
                // Buchstaben-Shortcut: matche gegen item.key (case-insensitive).
                if (typeof e.key === 'string' && e.key.length === 1
                        && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    const k = e.key.toUpperCase()
                    const item = cm.items.find(it => it.key === k)
                    if (item) await this._invokeContextMenuItem(item)
                }
                return
            }
        }
    }

    /**
     * Fuehrt ein Context-Menu-Item aus: Menu schliessen, Aktion routen.
     * Aktionen wie 'newProject'/'newFolder' operieren auf dem currentFolder,
     * andere Aktionen ueber _selected*() — die Selektion wurde im
     * handleContextMenu auf das Target-Item gesetzt.
     */
    async _invokeContextMenuItem(item) {
        this._closeContextMenu()
        await this._executeAction(item.action)
    }

    /**
     * Tastatur-Handler fuer den Such-Modus.
     */
    async _handleSearchKey(e) {
        switch (e.key) {
            case 'Escape':
                if (this._searchText) {
                    // 1. ESC: nur Text leeren (Spec §8.2). Fokus bleibt
                    // in der Box, damit der User direkt weitertippen kann.
                    this._searchText = ''
                    this._searchResults = []
                    this._searchSelectedIndex = -1
                    this._render()
                } else {
                    // 2. ESC bei leerem Text: Dashboard schliessen.
                    this.close()
                }
                return

            case 'Enter':
                if (this._searchResults.length === 0) return
                {
                    const idx = this._searchSelectedIndex >= 0
                        ? this._searchSelectedIndex : 0
                    await this._openSearchResult(idx)
                }
                return

            case 'ArrowDown':
                if (this._searchResults.length === 0) return
                this._searchSelectedIndex = this._searchSelectedIndex < 0
                    ? 0
                    : Math.min(this._searchSelectedIndex + 1, this._searchResults.length - 1)
                this._ensureSearchSelectionVisible()
                this._render()
                return

            case 'ArrowUp':
                if (this._searchResults.length === 0) return
                this._searchSelectedIndex = this._searchSelectedIndex <= 0
                    ? 0
                    : this._searchSelectedIndex - 1
                this._ensureSearchSelectionVisible()
                this._render()
                return

            case 'Tab':
                // Tab raus aus der Box — Such-Text bleibt, Fokus weg.
                this._deactivateSearch()
                return

            case 'Backspace':
                if (this._searchText.length > 0) {
                    this._searchText = this._searchText.slice(0, -1)
                    this._recomputeSearchResults()
                    this._render()
                }
                return

            default:
                // Druckbares Zeichen → Filter erweitern.
                if (typeof e.key === 'string' && e.key.length === 1
                        && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    this._searchText += e.key
                    this._recomputeSearchResults()
                    this._render()
                }
                return
        }
    }

    /**
     * Berechnet die globale Treffer-Liste neu — Substring-Match (case-
     * insensitive) auf `project.title` ueber alle aktiven Projekte. Pfade
     * werden ueber `getFolderPath` aufgeloest (sync via _folders-Cache),
     * sodass jedes Treffer-Item seine Pfad-Segmente schon mitbringt.
     * Trash-Projekte sind hier bewusst nicht dabei (Spec §9 — Trash ist
     * eine separate Ansicht ohne Cross-Folder-Verkettung).
     */
    _recomputeSearchResults() {
        const q = this._searchText.trim().toLowerCase()
        if (!q) {
            this._searchResults = []
            this._searchSelectedIndex = -1
            return
        }
        const byId = new Map(this._folders.map(f => [f.id, f]))
        const segmentsFor = (folderId) => {
            const segs = ['My Projects']
            if (!folderId) return segs
            const chain = []
            let cur = folderId
            const guard = new Set()
            while (cur && !guard.has(cur)) {
                guard.add(cur)
                const f = byId.get(cur)
                if (!f) break
                chain.unshift(f.name || 'Untitled')
                cur = f.parentFolderId || null
            }
            return segs.concat(chain)
        }
        const results = []
        for (const p of this._projects) {
            const title = (p.title || '').toLowerCase()
            if (!title.includes(q)) continue
            results.push({
                project: p,
                segments: segmentsFor(p.folderId || null)
            })
        }
        // Alphabetisch nach Titel, sekundaer nach Pfad.
        results.sort((a, b) => {
            const ta = (a.project.title || '').toLowerCase()
            const tb = (b.project.title || '').toLowerCase()
            if (ta !== tb) return ta.localeCompare(tb)
            return a.segments.join(' / ').toLowerCase()
                .localeCompare(b.segments.join(' / ').toLowerCase())
        })
        this._searchResults = results
        // Selektion fix halten wenn der Index noch existiert, sonst -1.
        if (this._searchSelectedIndex >= results.length) {
            this._searchSelectedIndex = -1
        }
    }

    /**
     * Haelt den selektierten Treffer (Such-Modus) im Viewport. Wird nach
     * jedem Pfeil-Update gerufen — sonst wandert der Index aus dem
     * sichtbaren Bereich, weil `_searchScrollOffset` sonst nur durch
     * Wheel veraendert wird. Symmetrisch zu `_ensureSelectionVisible`
     * (Folder-Modus), aber 2 Cell-Zeilen pro Item.
     */
    _ensureSearchSelectionVisible() {
        if (!this._layout) return
        if (this._searchSelectedIndex < 0) return
        const visibleRows = Math.max(1, Math.floor(this._layout.listVisible / 2))
        if (this._searchSelectedIndex < this._searchScrollOffset) {
            this._searchScrollOffset = this._searchSelectedIndex
        } else if (this._searchSelectedIndex >= this._searchScrollOffset + visibleRows) {
            this._searchScrollOffset = this._searchSelectedIndex - visibleRows + 1
        }
    }

    /**
     * Oeffnet das Treffer-Item bei `idx`: such-Feld leeren, Projekt
     * direkt oeffnen (Dashboard schliesst, Spec §8.2).
     */
    async _openSearchResult(idx) {
        const item = this._searchResults[idx]
        if (!item) return
        const pid = item.project.id
        this._clearSearch()
        this.close()
        await this._onOpen?.(pid)
    }

    /**
     * Klick auf einen Treffer (kein Doppelklick): Such-Feld leeren, in den
     * Folder navigieren, Item selektieren (Spec §8.2 erster Bullet).
     */
    async _focusSearchResult(idx) {
        const item = this._searchResults[idx]
        if (!item) return
        const project = item.project
        const targetFolderId = project.folderId || null
        this._clearSearch()
        await this._setCurrentFolder(targetFolderId)
        // Selektion auf das Item legen.
        const entryIdx = this._entries.findIndex(
            e => e.type === 'project' && e.project.id === project.id
        )
        this._selectedIndex = entryIdx >= 0 ? entryIdx : 0
        this._scrollOffset = 0
        this._ensureSelectionVisible()
        this._render()
    }

    async open() {
        // Phase 6.0d: Soft-Keyboard schliessen, falls offen
        blurKeyboardSink()
        this.isOpen = true
        this._hoveredIndex = -1
        this._showTrash = false
        this._scrollOffset = 0
        this._wheelAccumulator = 0
        await this._loadData()
        // Folder-Default-Pyramide (Spec §4.2):
        //   1) aktuelles Projekt → dessen Folder (auch wenn = Root)
        //   2) sonst: zuletzt geoeffneter Folder (lastFolder in localStorage)
        //   3) sonst: Root
        // "Aktuelles Projekt gewinnt" — wenn das Projekt im Root liegt,
        // bleibt das Dashboard im Root, nicht auf lastFolder.
        const cur = this._currentProjectFolderId()
        let target
        if (cur !== undefined) {
            target = cur
        } else {
            target = this._loadLastFolder()
            // Existenz-Check: Folder koennte serverseitig geloescht worden sein
            if (target && !this._folders.find(f => f.id === target)) {
                target = null
            }
        }
        await this._setCurrentFolder(target)
        // Default-Selektion: aktuell aktives Projekt im jetzigen Folder
        // (Spec §4.1). Liegt ausserhalb → 0 (oder Parent wenn vorhanden).
        this._selectedIndex = this._findSelectedDefault()
        // Ersten Render durchlaufen lassen, damit `_layout` (incl. listVisible)
        // gefuellt ist — danach koennen wir die Default-Selektion in den
        // Viewport scrollen und ggf. einmal nachrendern. Der zweite Render ist
        // billig (sparse Overlay, kein DOM).
        this._render()
        const prevOffset = this._scrollOffset
        this._ensureSelectionVisible()
        if (this._scrollOffset !== prevOffset) this._render()

        // Background-Sync wie bisher
        if (this._syncService) {
            this._syncService.initialSync()
                .then(() => this._refresh())
                .catch(err => console.warn('Background sync failed:', err.message))
        }
    }

    close() {
        this.isOpen = false
        // Picker mit-schliessen, sonst lebt sein State (Filter, Index) bis
        // zum naechsten Open weiter und der naechste open() haette ggf.
        // einen aktiven Picker im Hintergrund.
        if (this._pathPicker) this._pathPicker.close()
        // Drag-State sauber wegraeumen.
        this._dragState = null
        this._dragCandidate = null
        this._justFinishedDrag = false
        // Such-State sauber wegraeumen (Stufe F).
        this._clearSearch()
        // Context-Menu (Stufe G).
        this._contextMenu = null
        // Top-Overlay freigeben, falls wir ihn fuer Picker oder Drag-Ghost
        // bemalt haben — sonst bleibt das Modal visuell stehen, bis
        // zufaellig jemand anders (Dialog) ihn ueberschreibt.
        if (this._dashboardOwnsOverlayTop && this._overlayTop) {
            this._overlayTop.deactivate()
            this._dashboardOwnsOverlayTop = false
        }
        // deactivate() statt clear() — setzt isActive=false und triggert
        // borderMayBeDirty, damit die Pane-Borders neu gezeichnet werden.
        // Sonst bleiben Dashboard-Reste an den Border-Linien stehen, bis
        // zufaellig ein Full-Render durchlaeuft.
        this._overlay.deactivate()
    }

    /**
     * Re-Layout + Re-Render bei Browser-Resize / Zoom / Font-Wechsel.
     * `borderMayBeDirty` zwingt den Render-Loop, die Pane-Borders neu zu
     * zeichnen — sonst bleiben Pixel-Reste an den alten Frame-Kanten stehen.
     */
    handleResize() {
        if (!this.isOpen) return
        this._overlay.borderMayBeDirty = true
        this._render()
    }

    // ───────────────────────────────────────────────────────────────
    // Data-Loading
    // ───────────────────────────────────────────────────────────────

    async _loadData() {
        // Drei unabhaengige IndexedDB-Reads parallel — sie sharen keinen
        // Transaktions-Lock und auch keinen Store, also kein Drosseln noetig.
        const [projects, trashProjects, folders] = await Promise.all([
            this._projectManager.getAllProjects(),
            this._projectManager.getDeletedProjects(),
            this._folderManager ? this._folderManager.getAllFolders() : Promise.resolve([])
        ])
        this._projects = projects
        this._trashProjects = trashProjects
        this._folders = folders
        this._sortProjects()
        this._rebuildEntries()
    }

    async _refresh() {
        if (!this.isOpen) return
        const prevKey = this._entryKey(this._currentList()[this._selectedIndex])
        await this._loadData()
        // Selektion stabil halten ueber Refresh hinweg
        if (prevKey) {
            const list = this._currentList()
            const idx = list.findIndex(e => this._entryKey(e) === prevKey)
            if (idx >= 0) this._selectedIndex = idx
        }
        this._render()
    }

    /**
     * Eindeutige Key-Repraesentation eines Entries fuer Stable-Selection
     * ueber Refreshes hinweg. Trash-Eintraege sind nackte Projekte (keine
     * Folder dort), Folder-Modus-Eintraege sind getaggte Entry-Objekte.
     */
    _entryKey(entry) {
        if (!entry) return null
        if (this._showTrash) return `p:${entry.id}`
        if (entry.type === 'parent') return 'parent'
        if (entry.type === 'folder') return `f:${entry.folder.id}`
        if (entry.type === 'project') return `p:${entry.project.id}`
        return null
    }

    /**
     * Aktive Liste: im Trash nackte Projekt-Objekte (wie Stufe B), sonst
     * die kombinierten Entries des aktuellen Folders (siehe `_rebuildEntries`).
     */
    _currentList() {
        return this._showTrash ? this._trashProjects : this._entries
    }

    /**
     * Aktive Liste **inklusive Edit-Zeile** (falls editState aktiv).
     * Render- und Mouse-Hit-Pfade nutzen diese Variante, damit der Edit-
     * Slot wie eine echte Zeile mit Index behandelt wird; Action-Pfade
     * bleiben bei `_currentList()` ohne Edit-Zeile.
     *
     * - newFolder: prepended an Position 0 (Root) bzw. 1 (Sub-Folder, nach Parent)
     * - renameFolder/renameProject: ersetzt das Target an seinem Index
     */
    _visibleEntries() {
        if (this._showTrash) return this._trashProjects
        const es = this._editState
        const base = this._entries
        if (!es) return base
        if (es.mode === 'newFolder' || es.mode === 'newProject') {
            const result = base.slice()
            const insertAt = this._currentFolderId ? 1 : 0
            const type = es.mode === 'newFolder' ? 'editFolder' : 'editProject'
            result.splice(insertAt, 0, { type })
            return result
        }
        if (es.mode === 'renameFolder') {
            return base.map(e =>
                (e.type === 'folder' && e.folder.id === es.target)
                    ? { type: 'editFolder', folder: e.folder }
                    : e
            )
        }
        if (es.mode === 'renameProject') {
            return base.map(e =>
                (e.type === 'project' && e.project.id === es.target)
                    ? { type: 'editProject', project: e.project }
                    : e
            )
        }
        return base
    }

    /**
     * Liefert den Folder, in dem das aktuell aktive Projekt liegt:
     *   - undefined   = es gibt kein aktives Projekt (Caller soll lastFolder
     *                   o.ae. als Fallback nutzen)
     *   - null        = aktives Projekt liegt im Root
     *   - string      = Folder-ID
     *
     * `undefined` vs `null` ist hier wichtig: die Open-Logik unterscheidet,
     * ob "aktuelles Projekt im Root" oder "kein aktuelles Projekt"
     * vorliegt — beide Faelle haetten sonst `null` zurueckgeliefert und
     * die `lastFolder`-Fallback-Logik wuerde im falschen Fall greifen.
     */
    _currentProjectFolderId() {
        const currentId = this._projectManager.currentProjectId
        if (!currentId) return undefined
        const p = this._projects.find(p => p.id === currentId)
        if (!p) return undefined
        return p.folderId || null
    }

    /**
     * Wechselt den aktuellen Folder, baut die Entry-Liste neu auf und setzt
     * den Folder-Pfad fuer den Breadcrumb. Selektion wird vom Aufrufer
     * gesetzt (typisch: 0 = oberster Eintrag, also Parent-Entry im Sub-Folder
     * bzw. erstes Item im Root).
     * @param {string|null} folderId - null = Root
     */
    async _setCurrentFolder(folderId) {
        this._currentFolderId = folderId || null
        this._currentFolderPath = (this._folderManager && this._currentFolderId)
            ? await this._folderManager.getFolderPath(this._currentFolderId)
            : []
        this._rebuildEntries()
        // lastFolder im localStorage spiegeln, damit beim naechsten Open
        // (ohne aktives Projekt) dort wieder gelandet wird. Spec §4.2.
        this._persistLastFolder(this._currentFolderId)
    }

    /**
     * Baut `this._entries` aus den geladenen Folders + Projects des aktuellen
     * Folders neu auf. Sortiert nach aktueller Sort-Spalte (Folder und
     * Projekte mit-sortiert, Spec §3.3). Parent-Eintrag immer ganz oben.
     */
    _rebuildEntries() {
        const folderId = this._currentFolderId || null
        const subFolders = this._folders.filter(
            f => (f.parentFolderId || null) === folderId
        )
        const projectsHere = this._projects.filter(
            p => (p.folderId || null) === folderId
        )

        const entries = []
        for (const f of subFolders) {
            entries.push({ type: 'folder', folder: f })
        }
        for (const p of projectsHere) {
            entries.push({ type: 'project', project: p })
        }
        entries.sort((a, b) => this._compareEntries(a, b))

        if (folderId) {
            entries.unshift({ type: 'parent' })
        }
        this._entries = entries
    }

    _findSelectedDefault() {
        const currentId = this._projectManager.currentProjectId
        const list = this._currentList()
        if (currentId) {
            const idx = list.findIndex(e => {
                if (this._showTrash) return e.id === currentId
                return e.type === 'project' && e.project.id === currentId
            })
            if (idx >= 0) return idx
        }
        // Sonst: erster Eintrag, falls vorhanden. Parent-Eintrag (Index 0
        // im Sub-Folder) ist OK als Default — der User sieht sofort, dass
        // er "irgendwo" steckt, nicht im Root.
        return 0
    }

    // ───────────────────────────────────────────────────────────────
    // Sortierung
    // ───────────────────────────────────────────────────────────────

    _sortProjects() {
        // Folder-Modus sortieren wir nicht hier — passiert in _rebuildEntries
        // auf der zusammengefuehrten Entries-Liste, damit Folder + Projekte
        // gemeinsam sortiert werden (Spec §3.3). Trash bleibt eine flache
        // Liste sortiert nach deletedAt desc.
        this._trashProjects.sort((a, b) => {
            const va = new Date(a.deletedAt || 0).getTime()
            const vb = new Date(b.deletedAt || 0).getTime()
            return vb - va
        })
    }

    /**
     * Vergleicht zwei Entries (Folder oder Projekt) anhand der aktuellen
     * Sort-Spalte. Folder-spezifische Werte (Spec §3.4):
     *  - Size:      Folder zaehlen als 0 (Spalte ist bei Folder leer)
     *  - Modified:  juengstes updatedAt aller Projekte im Unterbaum, rekursiv
     *  - Created:   folder.createdAt
     */
    _compareEntries(a, b) {
        const dir = this._sortOrder === 'asc' ? 1 : -1
        const va = this._sortValue(a)
        const vb = this._sortValue(b)
        if (this._sortBy === 'name') {
            return String(va).localeCompare(String(vb)) * dir
        }
        return ((va || 0) - (vb || 0)) * dir
    }

    _sortValue(entry) {
        const isFolder = entry.type === 'folder'
        const obj = isFolder ? entry.folder : entry.project
        switch (this._sortBy) {
            case 'name':
                return (isFolder ? entry.folder.name : entry.project.title || '').toLowerCase()
            case 'size':
                return isFolder ? 0 : (entry.project.sizeBytes || 0)
            case 'created':
                return new Date(obj.createdAt || 0).getTime()
            case 'updated':
            default:
                if (isFolder) return this._folderRecentUpdated(entry.folder.id)
                return new Date(entry.project.updatedAt || 0).getTime()
        }
    }

    /**
     * Liefert das juengste `updatedAt`-Timestamp (Millisekunden) aller
     * Projekte im Unterbaum eines Folders, rekursiv inkl. dem Folder selbst
     * (Spec §3.4). 0 wenn der Unterbaum leer ist.
     */
    _folderRecentUpdated(folderId) {
        const all = this._folders
        // Descendant-Set in-memory (vermeidet asynchronen FolderManager-Call
        // im sort-comparator — der laeuft potentiell O(n log n) Mal).
        const descendants = new Set([folderId])
        const childrenByParent = new Map()
        for (const f of all) {
            const p = f.parentFolderId || null
            if (!childrenByParent.has(p)) childrenByParent.set(p, [])
            childrenByParent.get(p).push(f.id)
        }
        const queue = [folderId]
        while (queue.length > 0) {
            const cur = queue.shift()
            const kids = childrenByParent.get(cur) || []
            for (const kid of kids) {
                if (!descendants.has(kid)) {
                    descendants.add(kid)
                    queue.push(kid)
                }
            }
        }
        let recent = 0
        for (const p of this._projects) {
            // Root-Projekte (folderId null/undefined) gehoeren per Definition
            // zu keinem Folder-Subtree — descendants enthaelt nur Folder-IDs
            // (Strings, kein null), darum trifft .has(falsy) ohnehin nicht.
            if (descendants.has(p.folderId)) {
                const t = new Date(p.updatedAt || 0).getTime()
                if (t > recent) recent = t
            }
        }
        return recent
    }

    _cycleSort(field) {
        if (!SORT_FIELDS.includes(field)) return
        if (this._sortBy === field) {
            // Toggle desc ↔ asc auf derselben Spalte
            this._sortOrder = this._sortOrder === 'desc' ? 'asc' : 'desc'
        } else {
            // Neue Spalte: startet immer mit desc (Spec §3.3)
            this._sortBy = field
            this._sortOrder = 'desc'
        }
        this._persistSort()
        this._sortProjects()
        this._rebuildEntries()
        this._render()
    }

    _loadSortFromStorage() {
        try {
            const raw = localStorage.getItem(LS_SORT)
            if (!raw) return
            const { by, order } = JSON.parse(raw)
            if (SORT_FIELDS.includes(by)) this._sortBy = by
            if (order === 'asc' || order === 'desc') this._sortOrder = order
        } catch { /* ignorieren */ }
    }

    _persistSort() {
        try {
            localStorage.setItem(LS_SORT, JSON.stringify({
                by: this._sortBy, order: this._sortOrder
            }))
        } catch { /* ignorieren */ }
    }

    /**
     * Liest die zuletzt offene Folder-ID aus localStorage. null fuer Root,
     * sonst Folder-UUID. Wird beim Open als Fallback gebraucht, wenn kein
     * aktives Projekt da ist (Spec §4.2).
     * @returns {string|null}
     */
    _loadLastFolder() {
        try {
            const raw = localStorage.getItem(LS_LAST_FOLDER)
            if (!raw) return null
            return raw === 'null' ? null : raw
        } catch { return null }
    }

    /**
     * Persistiert die zuletzt offene Folder-ID. null = Root.
     * @param {string|null} folderId
     */
    _persistLastFolder(folderId) {
        try {
            localStorage.setItem(LS_LAST_FOLDER, folderId == null ? 'null' : folderId)
        } catch { /* ignorieren */ }
    }

    // ───────────────────────────────────────────────────────────────
    // Rendering
    // ───────────────────────────────────────────────────────────────

    _render() {
        this._overlay.clear()

        const cols = this._overlay.cols
        const rows = this._overlay.rows
        const layout = this._computeLayout(cols, rows)
        this._layout = layout

        // Frame mit Title
        this._overlay.drawMenuFrame({
            x: layout.x, y: layout.y,
            width: layout.width, height: layout.height
        }, { title: 'Projects' })

        // Toggle-Tabs rechts oben im Border-Strip
        this._renderToggleTabs(layout)

        // Breadcrumb-Zeile (Stufe B: statisch — Folder kommen in Stufe C)
        this._renderBreadcrumb(layout)

        // Search-Box (Stufe F): rechts in der Breadcrumb-Zeile.
        this._renderSearchBox(layout)

        // Column-Header
        this._renderColumnHeader(layout)

        // Liste — entweder normale Folder-Sicht oder globale Such-Treffer
        // (Stufe F). Mode-Switch ist sticky an `_searchText`, nicht an
        // `_searchActive` — der User kann die Box deaktivieren ohne die
        // Treffer-Sicht zu verlieren.
        if (this._searchText) {
            this._renderSearchResults(layout)
        } else {
            this._renderList(layout)
        }

        // Drop-Target-Highlight (Stufe E): zusaetzlicher Stripe ueber dem
        // gehoverten Drop-Ziel, NACH _renderList damit der Stripe die
        // Listen-Stripes ueberlagert.
        this._renderDropTargetHighlight(layout)

        // Action-Bar
        this._renderActionBar(layout)

        this._overlay.isDirty = true
        this._overlay.isActive = true

        // PathPicker (Stufe D) und Drag-Ghost (Stufe E) leben beide auf dem
        // Top-Overlay (gleicher Layer wie der DialogManager), damit der
        // Renderer sie erst NACH dem Dashboard zeichnet — sonst scheinen
        // Dashboard-Stripes/Spans/Cells durch das Modal. PathPicker und
        // Drag sind mutually exclusive (Picker blockiert Drag-Start).
        this._renderTopOverlay()
    }

    /**
     * Bestueckt den overlayTop mit dem aktuellen Modal-Inhalt — entweder
     * dem PathPicker (Stufe D) oder dem Drag-Ghost (Stufe E). Beide sind
     * exklusiv: der Picker blockiert Drag-Start, ein Drag kann nicht im
     * Picker beginnen. Ohne diese Symmetrie bliebe ein altes Modal-Bild
     * stehen, bis zufaellig wer anders (Dialog) den Layer ueberschreibt.
     */
    _renderTopOverlay() {
        if (!this._overlayTop) {
            // Fallback: ohne Top-Overlay direkt auf das Base-Overlay rendern
            // (kosmetisch nicht ideal, weil dann doch wieder Inhalte
            // durchscheinen koennen).
            if (this._pathPicker && this._pathPicker.isActive) {
                this._pathPicker.render(this._overlay, {
                    glyphSeparator: this._gutterGlyph('forward'),
                    cursorVisible: this._cursor ? !!this._cursor.visible : true,
                    renderer: this._renderer
                })
            } else if (this._contextMenu) {
                this._renderContextMenu(this._overlay)
            } else if (this._dragState) {
                this._renderDragGhost(this._overlay)
            }
            return
        }

        const wantsTop = (this._pathPicker && this._pathPicker.isActive)
            || !!this._contextMenu
            || !!this._dragState

        if (!wantsTop) {
            if (this._dashboardOwnsOverlayTop) {
                this._overlayTop.deactivate()
                this._dashboardOwnsOverlayTop = false
            }
            return
        }

        this._overlayTop.clear()
        if (this._pathPicker && this._pathPicker.isActive) {
            this._pathPicker.render(this._overlayTop, {
                glyphSeparator: this._gutterGlyph('forward'),
                cursorVisible: this._cursor ? !!this._cursor.visible : true,
                renderer: this._renderer
            })
        } else if (this._contextMenu) {
            this._renderContextMenu(this._overlayTop)
        } else if (this._dragState) {
            this._renderDragGhost(this._overlayTop)
        }
        this._overlayTop.isActive = true
        this._overlayTop.isDirty = true
        this._dashboardOwnsOverlayTop = true
    }

    /**
     * Context-Menu (Stufe G, Spec §5.5): Rendert die kontextabhaengige
     * Item-Liste am Anchor-Punkt mit Auto-Snap an die Frame-Raender. Frame
     * + Items via Phase-1-Primitive (drawMenuFrame / drawMenuItem) — exakt
     * dasselbe Pattern wie die normalen Menues.
     */
    _renderContextMenu(overlay) {
        const cm = this._contextMenu
        if (!cm) return
        const items = cm.items
        if (items.length === 0) return

        // Breite: laengstes Label + "[K] " (4 Cells) + 2 Padding + 2 Border.
        const maxLabelLen = Math.max(...items.map(it => (it.label || '').length))
        const width = Math.max(12, maxLabelLen + 8)
        // Hoehe: items.length + 2 (Border oben/unten)
        const height = items.length + 2

        // Auto-Snap an den Dashboard-Frame-Rand. Wir clampen zum Editor-Rand,
        // nicht zum Dashboard-Frame — das Context-Menu darf den Frame leicht
        // ueberragen, weil es auf overlayTop liegt.
        const x = Math.max(0, Math.min(cm.anchorX, overlay.cols - width))
        const y = Math.max(0, Math.min(cm.anchorY, overlay.rows - height))

        overlay.drawMenuFrame(
            { x, y, width, height },
            { borderStyle: Style.MENU_BORDER, bgStyle: Style.MENU_BACKGROUND }
        )

        const itemX = x + 1
        const itemWidth = width - 2
        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            const isSelected = i === cm.hoveredIndex
            overlay.drawMenuItem(
                itemX, y + 1 + i,
                {
                    key: item.key, label: item.label,
                    hasSubmenu: false, isCurrent: false, isDisabled: false
                },
                { selected: isSelected, itemWidth }
            )
        }

        // Bounds fuer Hit-Tests cachen.
        cm.x = x; cm.y = y; cm.width = width; cm.height = height
        // Border-Layer nur dirty markieren, wenn sich die Menue-Geometry
        // tatsaechlich aendert (Hover-Wechsel re-rendert dasselbe Frame).
        overlay.markBorderDirtyIfModalBoundsChanged('dashboardContextMenu', {
            x, y, width, height
        })
    }

    /**
     * Rechtsklick (Stufe G, Spec §5.5). Ermittelt das Target unter dem
     * Cursor und oeffnet das Context-Menu mit den fuer das Target erlaubten
     * Aktionen. Rechtsklick ausserhalb des Frames schliesst das Dashboard
     * (analog Linksklick), Rechtsklick auf einen normalen Eintrag setzt
     * vorher die Selektion auf diesen Eintrag — die _action*-Methoden
     * operieren ueber _selectedProject()/_selectedFolder().
     *
     * @param {number} charX
     * @param {number} charY
     * @returns {boolean} true wenn das Menu geoeffnet wurde
     */
    handleContextMenu(charX, charY) {
        if (!this.isOpen || !this._layout) return false
        const L = this._layout
        // Ausserhalb des Frames → Dashboard schliessen — ausser im
        // Kein-Projekt-Zustand, dort blocken wir analog zum Linksklick.
        if (charX < L.x || charX >= L.x + L.width || charY < L.y || charY >= L.y + L.height) {
            if (this._isNoProjectState()) {
                this._pulseHint()
                return false
            }
            this.close()
            return false
        }
        // Modal-Layer blockt Context-Menu (Picker hat eigene Schliess-Logik).
        if (this._pathPicker && this._pathPicker.isActive) return false
        // Im Edit / Drag / Such-Box-Fokus / Such-Mode kein Context-Menu.
        if (this._editState) return false
        if (this._dragState || this._dragCandidate) return false
        if (this._searchActive || this._searchText) return false

        // Target ermitteln: Klick auf Listen-Zeile oder leeren Bereich?
        let entry = null
        let isEmpty = true
        if (charX >= L.innerX && charX < L.innerX + L.innerWidth
                && charY >= L.listStartY && charY < L.listEndY) {
            const idx = (charY - L.listStartY) + this._scrollOffset
            const list = this._currentList()
            if (idx >= 0 && idx < list.length) {
                const e = list[idx]
                if (this._showTrash) {
                    entry = { type: 'project', project: e }
                } else if (e && e.type !== 'editFolder' && e.type !== 'editProject') {
                    entry = e
                }
                if (entry) {
                    isEmpty = false
                    this._selectedIndex = idx
                }
            }
        }

        const items = this._computeContextMenuItems({
            entry, isEmpty, isTrash: this._showTrash
        })
        if (items.length === 0) return false

        this._contextMenu = {
            items,
            anchorX: charX, anchorY: charY,
            x: charX, y: charY, width: 0, height: 0,
            hoveredIndex: 0,
            targetEntry: entry,
            targetIsEmpty: isEmpty
        }
        this._render()
        return true
    }

    /**
     * Schliesst das Context-Menu (Stufe G). Top-Overlay wird im naechsten
     * Render-Lauf automatisch deaktiviert (siehe _renderTopOverlay).
     */
    _closeContextMenu() {
        if (!this._contextMenu) return
        this._contextMenu = null
        this._render()
    }

    /**
     * Berechnet die Item-Liste fuer das Context-Menu (Stufe G, Spec §5.5).
     * Nur erlaubte Items werden gezeigt — disabled-Items existieren nicht.
     *
     * @param {Object} target
     *   @param {Object|null} target.entry      - Listen-Eintrag (folder/project/parent) oder null
     *   @param {boolean} target.isEmpty        - true bei Rechtsklick auf leeren Bereich
     *   @param {boolean} target.isTrash        - true im Trash-Modus
     * @returns {Array<{key, label, action}>}
     */
    _computeContextMenuItems(target) {
        const hasFolderManager = !!this._folderManager
        const items = []

        if (target.isTrash) {
            if (target.entry && target.entry.type === 'project') {
                items.push({ key: ACTIONS.RESTORE,     label: 'Restore',            action: 'restore' })
                items.push({ key: ACTIONS.PERM_DELETE, label: 'Permanently Delete', action: 'permDelete' })
            }
            // Empty Trash ist immer als unterster Eintrag dabei (Spec §5.5),
            // sofern es im Trash ueberhaupt etwas zu loeschen gibt.
            if (this._trashProjects.length > 0) {
                items.push({ key: ACTIONS.EMPTY_TRASH, label: 'Empty Deleted', action: 'emptyTrash' })
            }
            return items
        }

        // Folder-Modus.
        if (target.isEmpty || !target.entry) {
            items.push({ key: ACTIONS.NEW_PROJECT, label: 'New Project', action: 'newProject' })
            if (hasFolderManager) {
                items.push({ key: ACTIONS.NEW_FOLDER, label: 'New Folder', action: 'newFolder' })
            }
            return items
        }

        if (target.entry.type === 'parent') {
            items.push({ key: ACTIONS.OPEN, label: 'Open', action: 'open' })
            return items
        }

        if (target.entry.type === 'folder') {
            items.push({ key: ACTIONS.OPEN, label: 'Open', action: 'open' })
            if (hasFolderManager) {
                items.push({ key: ACTIONS.RENAME, label: 'Rename',     action: 'rename' })
                items.push({ key: ACTIONS.MOVE,   label: 'Move',       action: 'move' })
                items.push({ key: ACTIONS.DELETE, label: 'Delete All', action: 'delete' })
            }
            return items
        }

        if (target.entry.type === 'project') {
            const isCurrentOpen = this._tabManager?.isOpen?.(target.entry.project.id)
            items.push({
                key: ACTIONS.OPEN,
                label: isCurrentOpen ? 'Show' : 'Open',
                action: 'open'
            })
            if (isCurrentOpen) {
                items.push({ key: ACTIONS.CLOSE_TAB, label: 'Close', action: 'closeTab' })
            }
            items.push({ key: ACTIONS.RENAME,    label: 'Rename',    action: 'rename' })
            items.push({ key: ACTIONS.MOVE,      label: 'Move',      action: 'move' })
            items.push({ key: ACTIONS.DUPLICATE, label: 'Duplicate', action: 'duplicate' })
            items.push({ key: ACTIONS.DELETE,    label: 'Delete',    action: 'delete' })
            if (this._syncService) {
                items.push({
                    key: ACTIONS.CLOUD,
                    label: target.entry.project.cloudEnabled
                        ? 'Remove from Cloud' : 'Activate Cloud Sync',
                    action: 'cloud'
                })
            }
            return items
        }
        return items
    }

    /**
     * Kein-Projekt-Zustand: kein einziges Projekt liegt als Tab offen
     * (Spec §1.4 / §11.4-5). In diesem Zustand erscheint die Header-Hint-
     * Zeile und ESC/Klick-Ausserhalb sind blockiert.
     * @returns {boolean}
     */
    _isNoProjectState() {
        if (!this._tabManager) return false
        const tabs = this._tabManager.openTabs || []
        return tabs.length === 0
    }

    /**
     * Startet den 200-ms-Pulse der Header-Hint-Zeile (Spec §14.8). Wird
     * bei jedem blockierten ESC / Klick-Ausserhalb gerufen — gibt dem
     * User ein eindeutiges visuelles "geht gerade nicht"-Feedback. Plus
     * Ping als haptisches Aequivalent.
     */
    _pulseHint() {
        this._ping()
        this._hintPulseUntil = Date.now() + 200
        // Bestehenden Timer abbrechen — wenn der User schnell mehrmals
        // ESC drueckt, faengt jeder Tastendruck einen neuen Pulse an
        // statt dass der erste vorzeitig endet.
        if (this._hintPulseTimer) clearTimeout(this._hintPulseTimer)
        this._hintPulseTimer = setTimeout(() => {
            this._hintPulseTimer = null
            this._render()
        }, 220)
        this._render()
    }

    /**
     * Header-Hint-Zeile (Stufe J, Spec §1.4 / §14.8). Ersetzt im Kein-
     * Projekt-Zustand die Breadcrumb-Zeile. Stripe in
     * `MENU_ITEM_SELECTED_BG`, Text in `MENU_ITEM_KEY`-Akzent + bold.
     * Pulse-Variante (200ms nach blockierter Aktion): intensiver
     * `MENU_TITLE_BG`-Stripe.
     */
    _renderNoProjectHint(layout) {
        const isPulse = Date.now() < this._hintPulseUntil
        const bgStyle = isPulse ? Style.MENU_TITLE_BG : Style.MENU_ITEM_SELECTED_BG
        this._overlay.stripes.push({
            x: layout.innerX, y: layout.breadcrumbY,
            width: layout.innerWidth, bgStyle
        })
        const text = '▸ Open or create a project to continue.'
        this._overlay.addTextSpan(
            layout.innerX + 1, layout.breadcrumbY,
            text, Style.MENU_ITEM_KEY, layout.innerWidth - 2,
            { bold: true }
        )
        // Breadcrumb-Hit-Test-Bereich leer machen — kein Segment klickbar.
        this._layoutBreadcrumb = { y: layout.breadcrumbY, segments: [] }
    }

    /**
     * Search-Box (Stufe F): einzeilige Inline-Box rechts in der
     * Breadcrumb-Zeile mit Brackets in `MENU_ITEM_KEY`-Farbe (signalisiert
     * "interaktiv") und einem Text-Slot dazwischen. Inhalt:
     *   - aktiv + leer    → Cursor + leer
     *   - aktiv + Text    → Text + Cursor (rechtsbuendig wenn zu lang)
     *   - inaktiv + Text  → Text in MENU_ITEM
     *   - inaktiv + leer  → "Search…" in PANE_INFO (Placeholder)
     *
     * Im Trash wird keine Box gerendert (Trash hat keine Cross-Folder-
     * Suche, Spec §9).
     */
    _renderSearchBox(layout) {
        if (this._showTrash) { this._layoutSearch = null; return }
        // Stufe J: im Kein-Projekt-Zustand ersetzt die Hint-Zeile den
        // gesamten Breadcrumb-Streifen — Search-Box waere optisch im Weg.
        if (this._isNoProjectState()) { this._layoutSearch = null; return }
        const w = SEARCH_BOX_WIDTH
        const x = layout.innerX + layout.innerWidth - w
        const y = layout.breadcrumbY
        const bracketStyle = Style.MENU_ITEM_KEY
        this._overlay.setChar(x, y, '[', bracketStyle)
        this._overlay.setChar(x + w - 1, y, ']', bracketStyle)

        // Inhalt mit 1-Char Padding hinter [ und vor ].
        const contentX = x + 2
        const contentWidth = w - 4

        if (this._searchActive) {
            const text = this._searchText || ''
            const cursorVisible = this._cursor ? !!this._cursor.visible : true
            // Bei zu langem Text rechts abschneiden (links scrollt rein), damit
            // der Cursor am Ende sichtbar bleibt — Cmd+F-Konvention.
            const visibleText = text.length > contentWidth - 1
                ? text.slice(text.length - contentWidth + 1)
                : text
            this._overlay.addTextSpan(
                contentX, y, `${visibleText} `, Style.MENU_TITLE, contentWidth,
                {
                    cursorPos: Math.min(visibleText.length, contentWidth - 1),
                    cursorVisible
                }
            )
        } else if (this._searchText) {
            this._overlay.addTextSpan(contentX, y, this._searchText, Style.MENU_ITEM, contentWidth)
        } else {
            this._overlay.addTextSpan(contentX, y, 'Search…', Style.PANE_INFO, contentWidth)
        }

        this._layoutSearch = { x, y, width: w }
    }

    /**
     * Drop-Target-Highlight (Stufe E): einen zusaetzlichen Selection-
     * Stripe auf den Drop-Target zeichnen, sofern das Target gueltig ist.
     * Ungueltige Drops (Zyklus, No-Op) bekommen KEIN Highlight — der User
     * sieht so sofort, dass dort nichts passieren wird (Spec §7.4).
     *
     * Wird NACH `_renderList` gerufen; bei Folder-Targets ueberlagert der
     * Drop-Highlight-Stripe den Selection-/Hover-Stripe.
     */
    _renderDropTargetHighlight(layout) {
        if (!this._dragState) return
        const target = this._dragState.dropTarget
        if (!target || !target.ok) return
        if (target.kind === 'folder' || target.kind === 'parent') {
            const y = layout.listStartY + (target.rowIndex - this._scrollOffset)
            if (y < layout.listStartY || y >= layout.listEndY) return
            this._overlay.stripes.push({
                x: layout.innerX, y, width: layout.innerWidth,
                bgStyle: Style.MENU_ITEM_SELECTED_BG
            })
        } else if (target.kind === 'breadcrumb') {
            const B = this._layoutBreadcrumb
            if (!B) return
            const seg = B.segments[target.segIndex]
            if (!seg) return
            this._overlay.stripes.push({
                x: seg.x, y: B.y, width: seg.width,
                bgStyle: Style.MENU_ITEM_SELECTED_BG
            })
        }
    }

    /**
     * Drag-Ghost (Stufe E): kurze opake Zeile neben dem Cursor mit Glyph +
     * Name. Spec §7.2 spricht von "semi-transparent" — wir haben kein
     * Alpha im Canvas-Renderer, also opake Variante mit Selection-BG.
     * Folder-Ghost mit `►`-Glyph, Project-Ghost ohne Gutter.
     */
    _renderDragGhost(overlay) {
        if (!this._dragState) return
        const drag = this._dragState
        const glyph = drag.type === 'folder' ? this._gutterGlyph('forward') : ''
        const text = glyph ? `${glyph} ${drag.name}` : drag.name
        const isInvalid = drag.dropTarget && drag.dropTarget.ok === false
        // Cell-Breite mit etwas Padding links/rechts.
        const w = Math.min(60, this._textCellWidth(text) + 4)
        // Ghost rechts unten vom Cursor (2 Cell Offset), damit der Cursor
        // den Ghost-Text nicht ueberdeckt.
        const x = Math.max(0, Math.min(drag.x + 2, overlay.cols - w - 1))
        const y = Math.max(0, Math.min(drag.y, overlay.rows - 1))
        const bgStyle = Style.MENU_ITEM_SELECTED_BG
        const textStyle = isInvalid ? Style.PANE_INFO : Style.MENU_ITEM_SELECTED
        overlay.stripes.push({ x, y, width: w, bgStyle })
        overlay.addTextSpan(x + 1, y, text, textStyle, w - 2)
        // Drag-Ghost bewegt sich pro Mausmove einen Cell weiter — Border-Layer
        // nur dirty markieren, wenn Position oder Breite sich aendern. Beim
        // Drag-Ende ruft `_endDrag` clearModalBounds('dashboardDragGhost').
        overlay.markBorderDirtyIfModalBoundsChanged('dashboardDragGhost', {
            x, y, width: w, height: 1
        })
    }

    _computeLayout(cols, rows) {
        // Mindestbreite: alle fixen Spalten + Gaps + min Name + 2 Padding + 2 Border
        const fixedColsWidth =
              COL_GUTTER + COL_GAP + COL_NAME_MIN + COL_GAP
            + COL_SIZE + COL_GAP + COL_DATE + COL_GAP + COL_DATE + COL_GAP + COL_CLOUD
        const minWidth = fixedColsWidth + 4  // 2 Border + 2 Padding

        const width = Math.max(minWidth, Math.min(cols - 6, minWidth + 20))
        const height = Math.max(15, rows - FRAME_PAD_TOP - FRAME_PAD_BOTTOM)
        const x = Math.floor((cols - width) / 2)
        const y = FRAME_PAD_TOP

        // Inner-Area: 1 Border-Top + 1 Padding + Content + 1 Padding + 1 Border-Bottom.
        // Wir packen das Content-Drauf direkt unter Border-Top (kein vertikales Padding),
        // damit die Tabelle moeglichst viele Zeilen zeigt.
        const innerX = x + 2
        const innerWidth = width - 4
        const breadcrumbY = y + 1
        const sep1Y = y + 2
        const colHeaderY = y + 3
        const sep2Y = y + 4
        const listStartY = y + 5
        // Bottom-up: Border-Bottom = y+h-1. Darueber 2 Action-Zeilen
        // (Row1 = y+h-3, Row2 = y+h-2), darueber ein Separator (y+h-4).
        // listEndY ist exclusive — also gleich der Separator-Zeile.
        const actionBarHeight = 2
        const listEndY    = y + height - actionBarHeight - 2  // = y+h-4
        const actionBarY  = listEndY + 1                       // = y+h-3 (Row 1)
        const actionBar2Y = actionBarY + 1                     // = y+h-2 (Row 2, sitzt direkt ueber Border-Bottom)

        // Spaltenpositionen (links nach rechts).
        // Rechts immer eine Spalte fuer den Scrollbar reservieren (auch wenn
        // der Scrollbar gerade nicht sichtbar ist — sonst springt das Layout
        // beim Erscheinen) und einen Abstand davor, damit der Cloud-Text
        // visuell nicht in den Scrollbar laeuft.
        const colGutterX  = innerX
        // Kein COL_GAP zwischen Gutter und Name — die Gutter-Spalte (2 Cells)
        // ist im Prop-Mode breit genug fuer das ►/◁-Glyph plus etwas Pixel-
        // Luft, ein zusaetzlicher Gap-Cell wuerde nur Wirkung verschenken.
        const colNameX    = colGutterX + COL_GUTTER
        const scrollbarX  = innerX + innerWidth - SCROLLBAR_W
        const colsRightEdge = scrollbarX - COL_GAP  // Gap vor Scrollbar
        const colCloudX   = colsRightEdge - COL_CLOUD
        const colCreatedX = colCloudX - COL_GAP - COL_DATE
        const colModifiedX= colCreatedX - COL_GAP - COL_DATE
        const colSizeX    = colModifiedX - COL_GAP - COL_SIZE
        const colNameWidth = colSizeX - COL_GAP - colNameX

        return {
            x, y, width, height,
            innerX, innerWidth,
            breadcrumbY, sep1Y, colHeaderY, sep2Y,
            listStartY, listEndY,
            actionBarY, actionBar2Y,
            colGutterX, colNameX, colNameWidth, colSizeX, colModifiedX, colCreatedX, colCloudX,
            scrollbarX,
            listVisible: listEndY - listStartY
        }
    }

    // ── Header-Komponenten ────────────────────────────────────────

    _renderToggleTabs(layout) {
        // Rechts im Top-Border-Strip — auf der Border-Linie y=layout.y.
        // Aktive Tab: Selection-Stripe + Bold. Inaktive: schlichter Text.
        // Beide werden via addTextSpan mit clearBackground gerendert.
        const labelActive  = ' Active '
        const labelDeleted = ' Deleted '
        const totalLen = labelActive.length + labelDeleted.length + 1
        const startX = layout.x + layout.width - 2 - totalLen

        if (this._showTrash) {
            this._overlay.addTextSpan(startX, layout.y, labelActive, Style.MENU_ITEM, labelActive.length, { clearBackground: true })
            // Aktive Deleted-Tab: Stripe + Selected-Text. `text`+`textStyle`
            // machen den Stripe im Prop-Mode pixelgenau breit (Bold-aware).
            this._overlay.stripes.push({
                x: startX + labelActive.length + 1, y: layout.y,
                width: labelDeleted.length, bgStyle: Style.MENU_ITEM_SELECTED_BG,
                text: labelDeleted, textStyle: Style.MENU_ITEM_SELECTED
            })
            this._overlay.addTextSpan(startX + labelActive.length + 1, layout.y, labelDeleted, Style.MENU_ITEM_SELECTED, labelDeleted.length)
        } else {
            this._overlay.stripes.push({
                x: startX, y: layout.y,
                width: labelActive.length, bgStyle: Style.MENU_ITEM_SELECTED_BG,
                text: labelActive, textStyle: Style.MENU_ITEM_SELECTED
            })
            this._overlay.addTextSpan(startX, layout.y, labelActive, Style.MENU_ITEM_SELECTED, labelActive.length)
            this._overlay.addTextSpan(startX + labelActive.length + 1, layout.y, labelDeleted, Style.MENU_ITEM, labelDeleted.length, { clearBackground: true })
        }

        // Cache fuer Hit-Test
        this._layoutTabs = {
            projectsX: startX,
            projectsWidth: labelActive.length,
            trashX: startX + labelActive.length + 1,
            trashWidth: labelDeleted.length,
            y: layout.y
        }
    }

    _renderBreadcrumb(layout) {
        // Stufe J: im Kein-Projekt-Zustand ersetzt die Header-Hint-Zeile
        // den Breadcrumb komplett — der User soll genau einen Schritt
        // sehen (Open oder New Project).
        if (this._isNoProjectState()) {
            this._renderNoProjectHint(layout)
            for (let i = 0; i < layout.innerWidth; i++) {
                this._overlay.setChar(layout.innerX + i, layout.sep1Y, '─', Style.MENU_BORDER)
            }
            return
        }
        // Stufe C: dynamischer Breadcrumb fuer Folder-Pfad. Trash bleibt statisch.
        // Stufe F: bei aktiver Suche (Text gesetzt) statisch
        // "My Projects ▸ Search results" — die User-Intent ist temporaer
        // ein anderer Navigationskontext, der Folder-Pfad kommt zurueck
        // sobald die Suche geleert ist.
        // Segment-Positionen werden in `_layoutBreadcrumb` gecached fuer
        // Click-Hit-Tests (Stufe C-2 — bis dahin sind Klicks no-op, aber das
        // Layout steht schon).
        const segments = []
        if (this._showTrash) {
            segments.push({ id: '__trashroot', text: 'My Projects' })
            segments.push({ id: '__trash', text: 'Deleted' })
        } else if (this._searchText) {
            segments.push({ id: '__searchroot', text: 'My Projects' })
            segments.push({ id: '__search',     text: 'Search results' })
        } else {
            segments.push({ id: null, text: 'My Projects' })
            for (const f of this._currentFolderPath) {
                segments.push({ id: f.id, text: f.name || 'Untitled' })
            }
        }
        // Separator: dasselbe Symbol wie im Gutter (► Prop, > Mono).
        // Prop: nur ein Leerzeichen davor — der Pfeil klebt rechts ans
        // naechste Segment, proportionale Glyphen-Breite gibt ohnehin
        // genug optische Luft. Mono: Spaces auf BEIDEN Seiten, weil dort
        // alle Zellen gleich breit sind und `Foo >Bar` visuell asymmetrisch
        // wirkt.
        const isPropMode = !!(this._renderer && this._renderer.isProportional())
        const glyph = this._gutterGlyph('forward')
        const sep = isPropMode ? ` ${glyph}` : ` ${glyph} `
        // Span-Breiten im Prop-Mode anhand der echten Glyphen-Pixel-Breite
        // berechnen — sonst nimmt jeder Span eine volle Char-Cell pro
        // Codepoint, und kurze proportionale Segmente lassen grosse Luecken
        // zum naechsten Separator. _textCellWidth liefert Cell-Anzahl
        // basierend auf computeStringLayout.
        let x = layout.innerX
        // Stufe F: rechts in der Breadcrumb-Zeile sitzt die Search-Box.
        // Breadcrumb-Bereich verkuerzen, damit kein Ueberlapp passiert.
        // Im Trash gibt es keine Suche (Spec §9 — Trash ist flache Liste
        // ohne Cross-Folder-Kontext), dann nimmt der Breadcrumb die volle
        // Breite.
        const reserveSearchBox = !this._showTrash
        const rightEdge = reserveSearchBox
            ? layout.innerX + layout.innerWidth - SEARCH_BOX_WIDTH - 1
            : layout.innerX + layout.innerWidth
        const cached = []
        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i]
            const remaining = rightEdge - x
            if (remaining <= 0) break
            const len = Math.min(this._textCellWidth(seg.text), remaining)
            this._overlay.addTextSpan(x, layout.breadcrumbY, seg.text, Style.MENU_ITEM, len)
            cached.push({ id: seg.id, x, width: len })
            x += len
            if (i < segments.length - 1) {
                const sepLen = Math.min(this._textCellWidth(sep), rightEdge - x)
                if (sepLen <= 0) break
                // Separator-Glyph im gleichen Style wie die Breadcrumb-Segmente,
                // sonst rutscht das Dreieck farblich aus dem Pfad raus.
                this._overlay.addTextSpan(x, layout.breadcrumbY, sep, Style.MENU_ITEM, sepLen)
                x += sepLen
            }
        }
        this._layoutBreadcrumb = { y: layout.breadcrumbY, segments: cached }
        // Trennlinie
        for (let i = 0; i < layout.innerWidth; i++) {
            this._overlay.setChar(layout.innerX + i, layout.sep1Y, '─', Style.MENU_BORDER)
        }
    }

    /**
     * Liefert das richtige Pfeil-Glyph fuer den aktuellen Rendering-Mode.
     *
     * - Prop forward: ► — BLACK RIGHT-POINTING POINTER (U+25BA).
     *   Bewusst NICHT das Spec-▶ (U+25B6), weil das ein Emoji-Codepoint
     *   ist (Play-Button) und macOS/Chrome es als Apple-Color-Emoji
     *   rendert. ► ist als POINTER klassifiziert (nicht TRIANGLE) und
     *   damit nicht emoji-eligible — rendert als fettes Text-Glyph in
     *   derselben Gewichtsklasse wie die Sort-Pfeile ▲/▼.
     * - Prop back: ◁ — WHITE LEFT-POINTING TRIANGLE (U+25C1, outline).
     *   ◀ (U+25C0, BLACK) ist ein Emoji-Codepoint (Reverse-Button), ◄
     *   (BLACK POINTER) rendert in EB Garamond schmaler als ►. ◁ ist
     *   outline, nicht emoji, geometrisch sauber. Stilistisch asymmetrisch
     *   zum gefuellten ►, was zum Funktionskontrast passt: Folder = aktiv/
     *   prominent (fett), Parent-Eintrag = gedimmt/dezent (outline).
     * - Mono: > / < — ASCII-Fallback, weil Dreieck-Glyphen im Mono-Mode
     *   einer einzelnen Cell breiter waeren als die Cell-Breite zulaesst.
     *
     * @param {'forward'|'back'} direction
     */
    _gutterGlyph(direction) {
        const isProp = !!(this._renderer && this._renderer.isProportional())
        if (direction === 'forward') return isProp ? '►' : '>'
        if (direction === 'back')    return isProp ? '◁' : '<'
        return ''
    }

    /**
     * Berechnet die char-cell-Breite eines Text-Strings im aktuellen
     * Rendering-Mode. Im Mono == Codepoint-Count, im Prop wird die
     * Pixel-Breite des proportional gerenderten Glyphen-Strings auf
     * Cells aufgerundet. Wird vor allem fuer Breadcrumb-Segmente
     * gebraucht, damit die Segmente direkt nebeneinander sitzen ohne
     * die Mono-Cell-Luecken zwischen ihnen.
     * @param {string} text
     * @returns {number}
     */
    _textCellWidth(text) {
        if (!text) return 0
        const isProp = !!(this._renderer && this._renderer.isProportional())
        const codepoints = Array.from(text).length
        if (!isProp) return codepoints
        const metrics = this._renderer._activeMetrics?.(4)
        const cache = metrics?.cache
        if (!cache) return codepoints
        const cw = this._renderer.charWidth || 1
        const scale = metrics.scaleFactor || 1
        const lay = computeStringLayout(text, cache, cache.baseStep, null)
        return Math.max(1, Math.ceil((lay.totalWidth * scale) / cw))
    }

    _renderColumnHeader(layout) {
        // Spalten-Header in MENU_ITEM_KEY-Style (Akzent-Farbe, gibt der Liste Rhythmus).
        const isActive = (field) => this._sortBy === field
        const arrow = (field) => {
            if (!isActive(field)) return ''
            return this._sortOrder === 'desc' ? ' ▼' : ' ▲'
        }

        const headerStyle = Style.MENU_ITEM_KEY
        // Im Deleted-View heisst die Spalte "Expires in" statt "Modified",
        // weil die Zellen dort die verbleibenden Tage bis zur permanenten
        // Loeschung zeigen (siehe formatTrashExpiry).
        const modifiedLabel = this._showTrash ? 'Expires in' : 'Modified'
        this._overlay.addTextSpan(layout.colNameX, layout.colHeaderY,
            `Name${arrow('name')}`, headerStyle, layout.colNameWidth)
        this._overlay.addTextSpan(layout.colSizeX, layout.colHeaderY,
            `Size${arrow('size')}`, headerStyle, COL_SIZE)
        this._overlay.addTextSpan(layout.colModifiedX, layout.colHeaderY,
            `${modifiedLabel}${arrow('updated')}`, headerStyle, COL_DATE)
        this._overlay.addTextSpan(layout.colCreatedX, layout.colHeaderY,
            `Created${arrow('created')}`, headerStyle, COL_DATE)
        // Cloud-Header: Spec §3.1 — keine Beschriftung
        // (nichts emittieren)

        // Trennlinie unter dem Header
        for (let i = 0; i < layout.innerWidth; i++) {
            this._overlay.setChar(layout.innerX + i, layout.sep2Y, '─', Style.MENU_BORDER)
        }
    }

    // ── Liste ──────────────────────────────────────────────────────

    _renderList(layout) {
        const list = this._visibleEntries()
        const visible = layout.listVisible
        if (list.length === 0) return  // Empty-State ist absichtlich leer (Spec §3.6)

        // Scroll-Offset clampen — nicht mehr an Selektion koppeln. Mausrad-
        // Scrollen darf die Selektion aus dem Viewport schieben (Spec §4.4,
        // analog LinkPicker). Tastatur-Navigation ruft separat
        // `_ensureSelectionVisible()` auf, um den selektierten Eintrag im
        // Sichtbereich zu halten.
        const maxScroll = Math.max(0, list.length - visible)
        this._scrollOffset = Math.max(0, Math.min(this._scrollOffset, maxScroll))

        const openTabIds = new Set((this._tabManager?.openTabs || []))
        const isTrash = this._showTrash

        // Drag-Source-ID fuer Source-Row-Dimming (Stufe E). Beim aktiven
        // Drag erscheint die Originalzeile gedimmt, um optisch zu zeigen
        // welches Item gerade unterwegs ist (Spec §7.2).
        const dragSrcId = this._dragState ? this._dragState.id : null

        for (let i = 0; i < visible && this._scrollOffset + i < list.length; i++) {
            const idx = this._scrollOffset + i
            const entry = list[idx]
            const y = layout.listStartY + i
            const isSelected = idx === this._selectedIndex
            const isHovered = idx === this._hoveredIndex && !isSelected
            const isDimmed = dragSrcId && (
                (entry.type === 'folder' && entry.folder.id === dragSrcId)
                || (entry.type === 'project' && entry.project.id === dragSrcId)
            )

            if (isTrash) {
                this._renderListRow({
                    layout, y, project: entry, isSelected, isHovered,
                    isOpen: openTabIds.has(entry.id), isTrash: true,
                    isDimmed: false  // Trash hat kein Drag
                })
                continue
            }

            // Folder-Modus: Entry-Typen unterscheiden
            if (entry.type === 'parent') {
                this._renderParentRow({ layout, y, isSelected, isHovered })
            } else if (entry.type === 'folder') {
                this._renderFolderRow({ layout, y, folder: entry.folder, isSelected, isHovered, isDimmed })
            } else if (entry.type === 'editFolder' || entry.type === 'editProject') {
                this._renderEditRow({ layout, y, kind: entry.type, entry })
            } else {
                const project = entry.project
                this._renderListRow({
                    layout, y, project, isSelected, isHovered,
                    isOpen: openTabIds.has(project.id), isTrash: false,
                    isDimmed
                })
            }
        }

        // Scrollbar (rechts), wenn Liste laenger als Sichtbereich
        if (list.length > visible) {
            this._renderScrollbar(layout, list.length, visible)
        }
    }

    /**
     * Renderpfad fuer den Such-Modus (Stufe F). Pro Treffer zwei Cell-
     * Zeilen — oben das Projekt mit Spalten wie im Folder-Modus, darunter
     * eine eingerueckte Pfad-Subzeile in PANE_INFO mit dem vollen Folder-
     * Pfad. Selection-Stripe deckt beide Zeilen ab.
     */
    _renderSearchResults(layout) {
        const results = this._searchResults
        if (results.length === 0) {
            // Spec §3.6: zentriertes "(no matches)" in PANE_INFO.
            const msg = '(no matches)'
            const x = layout.innerX + Math.floor((layout.innerWidth - msg.length) / 2)
            this._overlay.addTextSpan(x, layout.listStartY, msg, Style.PANE_INFO, msg.length)
            return
        }

        const visibleRows = Math.floor(layout.listVisible / 2)
        const maxScroll = Math.max(0, results.length - visibleRows)
        this._searchScrollOffset = Math.max(0, Math.min(
            this._searchScrollOffset || 0, maxScroll
        ))
        const openTabIds = new Set((this._tabManager?.openTabs || []))
        const isProp = !!(this._renderer && this._renderer.isProportional())
        const sep = isProp ? ` ${this._gutterGlyph('forward')}` : ` ${this._gutterGlyph('forward')} `

        for (let i = 0; i < visibleRows && this._searchScrollOffset + i < results.length; i++) {
            const idx = this._searchScrollOffset + i
            const item = results[idx]
            const yTop = layout.listStartY + i * 2
            const yBot = yTop + 1
            const isSelected = idx === this._searchSelectedIndex
            const isHovered = idx === this._searchHoveredIndex && !isSelected
            const isOpen = openTabIds.has(item.project.id)
            // Selection-Stripe ueber beide Zeilen, Hover analog.
            if (isSelected || isHovered) {
                this._overlay.stripes.push({
                    x: layout.innerX, y: yTop, width: layout.innerWidth,
                    bgStyle: Style.MENU_ITEM_SELECTED_BG
                })
                this._overlay.stripes.push({
                    x: layout.innerX, y: yBot, width: layout.innerWidth,
                    bgStyle: Style.MENU_ITEM_SELECTED_BG
                })
            }
            // Projekt-Zeile (oben) — gleiche Logik wie _renderListRow.
            const rowStyle = isSelected ? Style.MENU_ITEM_SELECTED
                           : isOpen     ? Style.MENU_ITEM
                                        : Style.PANE_INFO
            const title = item.project.title || 'untitled'
            this._overlay.addTextSpan(layout.colNameX, yTop, title, rowStyle,
                layout.colNameWidth, { bold: isOpen })
            const sizeText = formatSize(item.project.sizeBytes || 0)
            if (sizeText) {
                this._overlay.addTextSpan(layout.colSizeX, yTop, sizeText, rowStyle,
                    COL_SIZE, { align: 'right' })
            }
            const modText = formatRelativeDate(item.project.updatedAt)
            if (modText) {
                this._overlay.addTextSpan(layout.colModifiedX, yTop, modText, rowStyle, COL_DATE)
            }
            const createdText = formatRelativeDate(item.project.createdAt)
            if (createdText) {
                this._overlay.addTextSpan(layout.colCreatedX, yTop, createdText, rowStyle, COL_DATE)
            }
            this._renderCloudCell(layout.colCloudX, yTop, item.project, rowStyle)
            // Pfad-Subzeile (unten) — Indent + PANE_INFO. Im Selected-State
            // bleibt die Subzeile in PANE_INFO, das harmoniert mit dem
            // helleren Selection-BG (Stripe abdeckt beide Zeilen).
            const pathText = item.segments.join(sep)
            const pathStyle = isSelected ? Style.MENU_ITEM_SELECTED : Style.PANE_INFO
            this._overlay.addTextSpan(
                layout.colNameX + 2, yBot, pathText, pathStyle,
                layout.innerWidth - (layout.colNameX + 2 - layout.innerX)
            )
        }

        // Scrollbar (rechts), wenn die Treffer-Liste laenger als sichtbar
        // ist — auf Visible-Items, nicht Cell-Zeilen.
        if (results.length > visibleRows) {
            this._renderScrollbar(layout, results.length, visibleRows)
        }
    }

    /**
     * Mappt eine y-Koordinate in der Listfache auf den Treffer-Index
     * (Such-Modus). Treffer sind 2 Cell-Zeilen hoch — die obere und die
     * untere Zeile gehoeren zum gleichen Treffer.
     * @returns {number} 0..len-1 oder -1 wenn ausserhalb / kein Treffer
     */
    _searchRowAt(charY) {
        if (!this._layout) return -1
        const L = this._layout
        if (charY < L.listStartY || charY >= L.listEndY) return -1
        const localY = charY - L.listStartY
        const offset = this._searchScrollOffset || 0
        const idx = Math.floor(localY / 2) + offset
        if (idx < 0 || idx >= this._searchResults.length) return -1
        return idx
    }

    /**
     * Inline-Edit-Zeile (Stufe C-3). Gleicher Stripe wie Selektion,
     * Gutter-Glyph nur fuer Folder-Edits, Name-Spalte ist der
     * SingleLineInput mit Cursor + Selection. Die uebrigen Spalten (Size,
     * Modified, Created, Cloud) bleiben bei Rename sichtbar — der User
     * editiert nur den Namen, der Rest gehoert weiter zum Datensatz.
     * Bei New Folder gibt es noch keinen Datensatz → andere Spalten leer.
     */
    _renderEditRow({ layout, y, kind, entry }) {
        const es = this._editState
        if (!es) return
        // Selektions-Stripe immer, damit der Edit-Slot visuell als aktiv
        // erkennbar bleibt — er IST die Selektion.
        this._overlay.stripes.push({
            x: layout.innerX, y, width: layout.innerWidth,
            bgStyle: Style.MENU_ITEM_SELECTED_BG
        })
        if (kind === 'editFolder') {
            this._overlay.addTextSpan(
                layout.colGutterX, y, this._gutterGlyph('forward'),
                Style.MENU_ITEM_SELECTED, COL_GUTTER
            )
        }
        const input = es.input
        input.setVisibleWidth(layout.colNameWidth)
        const state = input.getState()
        const visibleText = state.text.slice(state.scrollOffset, state.scrollOffset + layout.colNameWidth)
        const localCursor = Math.max(0, Math.min(
            state.cursorPos - state.scrollOffset,
            layout.colNameWidth
        ))
        // Blink-State aus dem globalen Cursor uebernehmen, sonst dauerhaft
        // sichtbar (fallback, falls cursor-Dep fehlt). main.js triggert im
        // Blink-Loop ein Re-Render, damit cursorVisible aktuell bleibt.
        const cursorVisible = this._cursor ? !!this._cursor.visible : true
        const opts = {
            cursorPos: localCursor,
            cursorVisible
        }
        if (state.selectionStart !== null && state.selectionEnd !== null
                && state.selectionStart !== state.selectionEnd) {
            const s = Math.min(state.selectionStart, state.selectionEnd) - state.scrollOffset
            const e = Math.max(state.selectionStart, state.selectionEnd) - state.scrollOffset
            opts.selectionStart = Math.max(0, Math.min(s, layout.colNameWidth))
            opts.selectionEnd   = Math.max(0, Math.min(e, layout.colNameWidth))
        }
        this._overlay.addTextSpan(
            layout.colNameX, y, visibleText, Style.MENU_ITEM_SELECTED, layout.colNameWidth, opts
        )

        // Restliche Spalten je nach Edit-Kind. Style ist immer
        // MENU_ITEM_SELECTED, weil die ganze Zeile selektiert ist.
        const colStyle = Style.MENU_ITEM_SELECTED
        if (kind === 'editProject' && entry?.project) {
            const project = entry.project
            const sizeText = formatSize(project.sizeBytes || 0)
            if (sizeText) {
                this._overlay.addTextSpan(layout.colSizeX, y, sizeText, colStyle, COL_SIZE, { align: 'right' })
            }
            const modText = formatRelativeDate(project.updatedAt)
            if (modText) {
                this._overlay.addTextSpan(layout.colModifiedX, y, modText, colStyle, COL_DATE)
            }
            const createdText = formatRelativeDate(project.createdAt)
            if (createdText) {
                this._overlay.addTextSpan(layout.colCreatedX, y, createdText, colStyle, COL_DATE)
            }
            this._renderCloudCell(layout.colCloudX, y, project, colStyle)
        } else if (kind === 'editFolder' && entry?.folder) {
            const folder = entry.folder
            // Size + Cloud bleiben leer (Spec §3.4 — Folder haben keine Size,
            // sind immer Cloud-synced).
            const recent = this._folderRecentUpdated(folder.id)
            if (recent > 0) {
                const txt = formatRelativeDate(new Date(recent).toISOString())
                if (txt) this._overlay.addTextSpan(layout.colModifiedX, y, txt, colStyle, COL_DATE)
            }
            const createdText = formatRelativeDate(folder.createdAt)
            if (createdText) {
                this._overlay.addTextSpan(layout.colCreatedX, y, createdText, colStyle, COL_DATE)
            }
        }
        // newFolder: kein entry — andere Spalten bleiben leer, kein Datensatz da.
    }

    /**
     * Parent-Eintrag (Spec §14.2): ◁ im Gutter, "back to parent folder"
     * in PARENT_ENTRY-Style (50 % grau, kursiv), restliche Spalten leer.
     * Selection-/Hover-Stripe identisch zu anderen Eintraegen. Gutter-Glyph
     * via addTextSpan, damit es im Prop-Mode proportional gerendert wird —
     * im Mono fallen wir auf das ASCII `<` zurueck, weil `◁` mehr als eine
     * Mono-Cell braucht.
     */
    _renderParentRow({ layout, y, isSelected, isHovered }) {
        if (isSelected || isHovered) {
            this._overlay.stripes.push({
                x: layout.innerX, y, width: layout.innerWidth,
                bgStyle: Style.MENU_ITEM_SELECTED_BG
            })
        }
        const labelStyle = isSelected ? Style.MENU_ITEM_SELECTED : Style.PARENT_ENTRY
        // Gutter-Glyph in derselben Farbe wie der Text dahinter, aber
        // ohne italic — PARENT_ENTRY's isItalic kursiviert sonst auch das
        // Dreieck, was visuell stoert. PANE_INFO hat dieselbe Farbe,
        // ohne italic. Bei Selektion uebernimmt MENU_ITEM_SELECTED.
        const glyphStyle = isSelected ? Style.MENU_ITEM_SELECTED : Style.PANE_INFO
        this._overlay.addTextSpan(
            layout.colGutterX, y, this._gutterGlyph('back'),
            glyphStyle, COL_GUTTER
        )
        this._overlay.addTextSpan(
            layout.colNameX, y, 'back to parent folder',
            labelStyle, layout.colNameWidth
        )
    }

    /**
     * Folder-Zeile: ▶ im Gutter (gleiche Farbe wie der Name), Name in
     * normaler Item-Farbe. Size + Cloud-Spalte bleiben leer (Spec §3.4).
     * Modified zeigt das juengste updatedAt im Unterbaum, Created das
     * Folder-eigene createdAt.
     */
    _renderFolderRow({ layout, y, folder, isSelected, isHovered, isDimmed = false }) {
        // Drag-Source: kein Stripe, gedimmter Text (Spec §7.2).
        if (isDimmed) {
            const rowStyle = Style.PANE_INFO
            this._overlay.addTextSpan(
                layout.colGutterX, y, this._gutterGlyph('forward'), rowStyle, COL_GUTTER
            )
            this._overlay.addTextSpan(
                layout.colNameX, y, folder.name || 'Untitled', rowStyle, layout.colNameWidth
            )
            const recent = this._folderRecentUpdated(folder.id)
            if (recent > 0) {
                const txt = formatRelativeDate(new Date(recent).toISOString())
                if (txt) this._overlay.addTextSpan(layout.colModifiedX, y, txt, rowStyle, COL_DATE)
            }
            const createdTxt = formatRelativeDate(folder.createdAt)
            if (createdTxt) {
                this._overlay.addTextSpan(layout.colCreatedX, y, createdTxt, rowStyle, COL_DATE)
            }
            return
        }
        if (isSelected || isHovered) {
            this._overlay.stripes.push({
                x: layout.innerX, y, width: layout.innerWidth,
                bgStyle: Style.MENU_ITEM_SELECTED_BG
            })
        }
        const rowStyle = isSelected ? Style.MENU_ITEM_SELECTED : Style.MENU_ITEM
        // Glyph in derselben Farbe wie der Name (Boris-Wunsch).
        this._overlay.addTextSpan(
            layout.colGutterX, y, this._gutterGlyph('forward'),
            rowStyle, COL_GUTTER
        )
        this._overlay.addTextSpan(
            layout.colNameX, y, folder.name || 'Untitled',
            rowStyle, layout.colNameWidth
        )
        // Size: leer (Spec §3.4)
        // Modified: juengstes updatedAt im Unterbaum, sonst leer
        const recent = this._folderRecentUpdated(folder.id)
        if (recent > 0) {
            const txt = formatRelativeDate(new Date(recent).toISOString())
            if (txt) this._overlay.addTextSpan(layout.colModifiedX, y, txt, rowStyle, COL_DATE)
        }
        // Created
        const createdTxt = formatRelativeDate(folder.createdAt)
        if (createdTxt) {
            this._overlay.addTextSpan(layout.colCreatedX, y, createdTxt, rowStyle, COL_DATE)
        }
        // Cloud: leer (Spec §3.4) — Folder sind immer cloud-synced, eigene
        // Spalten-Anzeige waere redundant.
    }

    _renderListRow({ layout, y, project, isSelected, isHovered, isOpen, isTrash, isDimmed = false }) {
        if (isDimmed) {
            // Drag-Source-Dimming (Spec §7.2) — kein Stripe, ganze Zeile in
            // PANE_INFO. Wichtig: keine Bold-Markierung fuer offene Projekte,
            // sonst sieht die Source nicht wirklich gedimmt aus.
            const rowStyle = Style.PANE_INFO
            const title = project.title || 'untitled'
            this._overlay.addTextSpan(layout.colNameX, y, title, rowStyle, layout.colNameWidth)
            const sizeText = formatSize(project.sizeBytes || 0)
            if (sizeText) {
                this._overlay.addTextSpan(layout.colSizeX, y, sizeText, rowStyle, COL_SIZE, { align: 'right' })
            }
            const modifiedText = isTrash
                ? formatTrashExpiry(project.deletedAt)
                : formatRelativeDate(project.updatedAt)
            if (modifiedText) {
                this._overlay.addTextSpan(layout.colModifiedX, y, modifiedText, rowStyle, COL_DATE)
            }
            const createdText = formatRelativeDate(project.createdAt)
            if (createdText) {
                this._overlay.addTextSpan(layout.colCreatedX, y, createdText, rowStyle, COL_DATE)
            }
            this._renderCloudCell(layout.colCloudX, y, project, rowStyle)
            return
        }
        // Selection-Stripe ueber die ganze innere Breite (analog drawMenuItem-Konvention)
        if (isSelected) {
            this._overlay.stripes.push({
                x: layout.innerX, y, width: layout.innerWidth,
                bgStyle: Style.MENU_ITEM_SELECTED_BG
            })
        } else if (isHovered) {
            // Hover-Stripe in Default-MENU_BACKGROUND ist visuell unsichtbar.
            // Stattdessen leicht das Selection-Stripe-BG halbtransparent —
            // hier reicht Selection-Stripe-Konstante, der Visual-Effekt ist
            // dezent abgeschwaecht durch fehlenden Text-Style-Wechsel.
            this._overlay.stripes.push({
                x: layout.innerX, y, width: layout.innerWidth,
                bgStyle: Style.MENU_ITEM_SELECTED_BG
            })
        }

        // Klare Hierarchie (Boris-Review):
        //   Farbe   = Status der Zeile (closed=dim, open=normal, selected=highlight)
        //   Bold    = "offen" (nur am Namen sichtbar)
        // Damit ist eine Zeile in EINEM einheitlichen Farb-Stil ueber alle
        // Spalten; der Name traegt zusaetzlich den Bold-Indikator wenn das
        // Projekt offen ist.
        //
        // Deleted-Modus braucht KEINE Spezialfaerbung: Trash-Projekte sind
        // ohnehin nie offen → fallen automatisch auf den dim-grauen Default,
        // und Selektion zeigt sich normal als Selection-Highlight. isTrash
        // bleibt nur fuer die Modified-Spalte (expires-Anzeige).
        const rowStyle = isSelected ? Style.MENU_ITEM_SELECTED
                       : isOpen     ? Style.MENU_ITEM
                       : Style.PANE_INFO
        // Bold-Override fuer den Namen: nur wenn offen (Stil MENU_ITEM_SELECTED
        // ist von Haus aus bold — daher bei selected-nicht-offen mit
        // bold:false explizit unterdruecken).
        const nameBold = isOpen
        const title = project.title || 'untitled'
        this._overlay.addTextSpan(layout.colNameX, y, title, rowStyle, layout.colNameWidth, { bold: nameBold })

        // Size (rechtsbuendig in der Spalte)
        const sizeText = formatSize(project.sizeBytes || 0)
        if (sizeText) {
            this._overlay.addTextSpan(layout.colSizeX, y, sizeText, rowStyle, COL_SIZE, { align: 'right' })
        }

        // Modified — im Trash: "expires in N days" statt updatedAt
        const modifiedText = isTrash
            ? formatTrashExpiry(project.deletedAt)
            : formatRelativeDate(project.updatedAt)
        if (modifiedText) {
            this._overlay.addTextSpan(layout.colModifiedX, y, modifiedText, rowStyle, COL_DATE)
        }

        // Created
        const createdText = formatRelativeDate(project.createdAt)
        if (createdText) {
            this._overlay.addTextSpan(layout.colCreatedX, y, createdText, rowStyle, COL_DATE)
        }

        // Cloud-Spalte (Spec §3.1, angepasst 2026-07-02)
        this._renderCloudCell(layout.colCloudX, y, project, rowStyle)
    }

    /**
     * Cloud-Spalte: "Cloud" wenn synchronisiert, sonst "local" — Erstnutzer
     * sollen sehen, dass ein Projekt nur auf diesem Geraet liegt
     * (Testphasen-Entscheidung 2026-07-02, ersetzt Spec §3.1 "sonst leer").
     * Im Papierkorb bleibt die Spalte fuer lokale Projekte leer.
     */
    _renderCloudCell(x, y, project, style) {
        if (project.cloudEnabled) {
            this._overlay.addTextSpan(x, y, 'Cloud', style, COL_CLOUD)
        } else if (!project.deletedAt) {
            this._overlay.addTextSpan(x, y, 'local', style, COL_CLOUD)
        }
    }

    _renderScrollbar(layout, total, visible) {
        const trackHeight = visible
        const thumbSize = Math.max(1, Math.round(trackHeight * trackHeight / total))
        const maxStart = Math.max(0, total - trackHeight)
        const thumbStart = maxStart > 0
            ? Math.round(this._scrollOffset * (trackHeight - thumbSize) / maxStart)
            : 0

        const isProp = this._renderer ? this._renderer.isProportional() : false

        if (!isProp) {
            // Mono-Mode: gleicher Look wie Editor/Console-Scrollbar.
            // Track ueber die ganze Listenhoehe als '░'-Cells, Thumb als
            // solide fillRect-Stripe (Renderer behandelt das).
            this._overlay.stripes.push({
                x: layout.scrollbarX, y: layout.listStartY,
                width: SCROLLBAR_W, height: trackHeight,
                bgStyle: Style.SCROLLBAR_TRACK,
                monoChar: '░'
            })
            if (thumbSize < trackHeight) {
                this._overlay.stripes.push({
                    x: layout.scrollbarX, y: layout.listStartY + thumbStart,
                    width: SCROLLBAR_W, height: thumbSize,
                    bgStyle: Style.SCROLLBAR_THUMB
                })
            }
            return
        }

        // Prop-Mode: Editor-Minimap-Pattern, simuliert per opaken Mischfarben
        // (kein globalAlpha, Boris-Wunsch). Werte (Inset, Alphas) sind
        // IDENTISCH zu MINIMAP-Konstanten und _renderMinimap in EditorView,
        // damit der Look exakt wie im Editor- und Console-Scrollbar aussieht.
        //
        // Editor stackt 3 Alpha-Layer:
        //   Underlayer (fg, 0.10) + Section (fg, 0.22) + Edge (fg, 0.45)
        // Im Inset-Bereich liegen Underlayer + Section uebereinander
        // (Section ist halbtransparent → leichter Aufheller), in den
        // Edge-Streifen Underlayer + Edge. Diese Stacks rechnen wir vorab
        // als opake Mischungen aus, damit ohne globalAlpha derselbe Eindruck
        // entsteht.
        const cw = this._renderer.charWidth
        const colPx0 = layout.scrollbarX * cw
        const colPxW = SCROLLBAR_W * cw
        const inset = Math.min(MINIMAP.INSET, Math.floor((colPxW - 1) / 2))

        const fgColor = this._renderer._resolveStyle(Style.EDITOR_TEXT).color
        const bgColor = this._renderer.colors.background

        // Außerhalb Viewport: nur Section-Layer (fg * 0.22).
        // Innerhalb Viewport, Inset: Section ueber Underlayer
        //   = (0.10*fg + 0.90*bg) * (1-0.22) + fg * 0.22
        //   = ~0.298 * fg + 0.702 * bg
        // Innerhalb Viewport, Edge-Streifen: Edge ueber Underlayer
        //   = (0.10*fg + 0.90*bg) * (1-0.45) + fg * 0.45
        //   = ~0.505 * fg + 0.495 * bg
        const trackOutsideColor = _mixColors(fgColor, bgColor, MINIMAP.SECTION_EDITABLE_ALPHA)
        const trackInsideColor  = _mixColors(fgColor, bgColor,
            MINIMAP.VIEWPORT_ALPHA * (1 - MINIMAP.SECTION_EDITABLE_ALPHA) + MINIMAP.SECTION_EDITABLE_ALPHA)
        const edgeInsideColor   = _mixColors(fgColor, bgColor,
            MINIMAP.VIEWPORT_ALPHA * (1 - MINIMAP.VIEWPORT_EDGE_ALPHA) + MINIMAP.VIEWPORT_EDGE_ALPHA)

        const thumbY = layout.listStartY + thumbStart
        const thumbHasGap = thumbSize < trackHeight

        // 1) Track-Bar im Inset, ueber die ganze Hoehe — section-Color
        this._overlay.stripes.push({
            x: layout.scrollbarX, y: layout.listStartY,
            width: SCROLLBAR_W, height: trackHeight,
            pixelX: colPx0 + inset,
            pixelW: colPxW - 2 * inset,
            bgColor: trackOutsideColor
        })

        if (!thumbHasGap) return

        // 2) Track-Bar im Thumb-Bereich aufhellen (= Section + Underlayer)
        this._overlay.stripes.push({
            x: layout.scrollbarX, y: thumbY,
            width: SCROLLBAR_W, height: thumbSize,
            pixelX: colPx0 + inset,
            pixelW: colPxW - 2 * inset,
            bgColor: trackInsideColor
        })
        // 3a) Edge links (Inset-Breite, Edge-Color)
        this._overlay.stripes.push({
            x: layout.scrollbarX, y: thumbY,
            width: SCROLLBAR_W, height: thumbSize,
            pixelX: colPx0, pixelW: inset,
            bgColor: edgeInsideColor
        })
        // 3b) Edge rechts (Inset-Breite, Edge-Color)
        this._overlay.stripes.push({
            x: layout.scrollbarX, y: thumbY,
            width: SCROLLBAR_W, height: thumbSize,
            pixelX: colPx0 + colPxW - inset, pixelW: inset,
            bgColor: edgeInsideColor
        })
    }

    // ── Action-Bar ─────────────────────────────────────────────────

    _renderActionBar(layout) {
        // Trennlinie ueber der Action-Bar
        const sepY = layout.actionBarY - 1
        for (let i = 0; i < layout.innerWidth; i++) {
            this._overlay.setChar(layout.innerX + i, sepY, '─', Style.MENU_BORDER)
        }

        const buttons = this._computeActionButtons()
        // Verteilung: erste Reihe = projekt-spezifische Aktionen, zweite Reihe = neu/global
        const row1 = []
        const row2 = []
        for (const b of buttons) {
            (b.row === 2 ? row2 : row1).push(b)
        }

        this._renderActionRow(layout, layout.actionBarY, row1)
        if (row2.length > 0) this._renderActionRow(layout, layout.actionBar2Y, row2)

        // Cache fuer Hit-Test
        this._layoutButtons = buttons
    }

    _computeActionButtons() {
        const isTrash = this._showTrash

        if (isTrash) {
            const sel = this._trashProjects[this._selectedIndex]
            const hasSelection = !!sel
            return [
                { key: ACTIONS.RESTORE,     label: 'Restore',            row: 1, disabled: !hasSelection, action: 'restore' },
                { key: ACTIONS.PERM_DELETE, label: 'Permanently Delete', row: 1, disabled: !hasSelection, action: 'permDelete' },
                { key: ACTIONS.EMPTY_TRASH, label: 'Empty Deleted',      row: 1, disabled: this._trashProjects.length === 0, action: 'emptyTrash' }
            ]
        }

        // Folder-Modus: Aktions-Verfuegbarkeit haengt vom Entry-Typ ab (Spec §5.3).
        const entry = this._entries[this._selectedIndex] || null
        const isParent  = entry?.type === 'parent'
        const isFolder  = entry?.type === 'folder'
        const isProject = entry?.type === 'project'
        const tabsOpen = (this._tabManager?.openTabs || [])
        const isCurrentOpen = isProject && tabsOpen.includes(entry.project.id)
        const hasFolderManager = !!this._folderManager

        // [O] Open: aktiv fuer alle drei Entry-Typen (fuer Projekte = Open
        // bzw. Show wenn schon offen; fuer Folder/Parent = navigieren).
        const buttons = [
            { key: ACTIONS.OPEN, label: isCurrentOpen ? 'Show' : 'Open', row: 1, disabled: !entry, action: 'open' }
        ]
        if (isCurrentOpen) {
            buttons.push({ key: ACTIONS.CLOSE_TAB, label: 'Close', row: 1, disabled: false, action: 'closeTab' })
        }
        // Rename: Projekt + Folder. Folder braucht zudem folderManager.
        buttons.push(
            { key: ACTIONS.RENAME,      label: 'Rename',      row: 1,
              disabled: !(isProject || (isFolder && hasFolderManager)),
              action: 'rename' },
            // Move (Stufe D): Projekt oder Folder. Folder braucht folderManager.
            { key: ACTIONS.MOVE,        label: 'Move',        row: 1,
              disabled: !(isProject || (isFolder && hasFolderManager)),
              action: 'move' },
            // Duplicate: nur Projekt (Spec §5.3 — Folder nicht duplizierbar)
            { key: ACTIONS.DUPLICATE,   label: 'Duplicate',   row: 1,
              disabled: !isProject, action: 'duplicate' },
            // Delete: Projekt → Trash; Folder → "Delete All" (rekursiv)
            { key: ACTIONS.DELETE,
              label: isFolder ? 'Delete All' : 'Delete',
              row: 1,
              disabled: isFolder ? !hasFolderManager : !isProject,
              action: 'delete' },
            // Cloud: nur Projekt (Folder sind immer cloud-synced, Spec §10.2).
            // Label kontext-abhaengig — als einziges Toggle in der Action-Bar
            // wird der naechste Schritt explizit benannt, damit der User vorab
            // sieht, ob er das Projekt in die Cloud schiebt oder lokal-only
            // macht.
            { key: ACTIONS.CLOUD,
              label: isProject && entry.project.cloudEnabled
                  ? 'Remove from Cloud' : 'Activate Cloud Sync',
              row: 1,
              disabled: !isProject || !this._syncService, action: 'cloud' },
            { key: ACTIONS.NEW_PROJECT, label: 'New Project', row: 2,
              disabled: false, action: 'newProject' },
            { key: ACTIONS.NEW_FOLDER,  label: 'New Folder',  row: 2,
              disabled: !hasFolderManager, action: 'newFolder' }
        )
        // Waehrend eines aktiven Inline-Edits sind ALLE Buttons disabled —
        // der User soll erst Enter/Escape druecken, bevor irgendwo anders
        // hinklickbar ist. Visuell wird das durch die Selection-Stripe-
        // Edit-Zeile ohnehin klar; die Button-Disable verhindert
        // versehentliche Klicks.
        if (this._editState) {
            for (const b of buttons) b.disabled = true
        }
        // Im Such-Modus sind alle Action-Buttons disabled (Spec §8.2 —
        // keine Selektion initial → alle Buttons aus). Auch nach
        // Pfeil-Navigation in der Treffer-Liste: der naechste sinnvolle
        // Schritt ist Enter (Open) oder Klick (Navigate); andere Aktionen
        // wuerden Source-Folder-Verwirrung erzeugen.
        if (this._searchText) {
            for (const b of buttons) b.disabled = true
        }
        return buttons
    }

    _renderActionRow(layout, y, buttons) {
        // Buttons nebeneinander, mit Gap dazwischen. Format: "[K] Label".
        // Hover-Logik analog drawMenuItem (Dialog/Menu): Selection-Stripe +
        // Selected-Styles bei Hover; disabled Buttons sind nicht hoverable.
        //
        // Label-Breite wird im Prop-Mode aus der echten Pixel-Breite des
        // proportional gerenderten Labels berechnet (auf Char-Cells aufgerundet).
        // Sonst rechnet `labelText.length + 1` mit Mono-Breiten und die
        // Abstaende werden ungleichmaessig — kurze Labels lassen mehr Luft
        // als lange.
        const isProp = !!(this._renderer && this._renderer.isProportional())
        const cw = this._renderer ? this._renderer.charWidth : 1
        const metrics = isProp && this._renderer ? this._renderer._activeMetrics(4) : null
        const cache = metrics && metrics.cache ? metrics.cache : null
        const scale = metrics ? metrics.scaleFactor : 1
        // Labels werden im Hover-Pfad in MENU_ITEM_SELECTED (bold) gerendert
        // — die Bold-Glyphen sind breiter als die Regular-Glyphen. Wir
        // dimensionieren die Buttons (und damit auch den Hover-Stripe)
        // konsequent in der Bold-Variante, damit der Stripe das ganze
        // gerenderte Label deckt. Bei nicht-hoverable Buttons (disabled)
        // reicht die Regular-Variante.
        const labelCharWidth = (label, bold) => {
            if (!isProp || !cache) return label.length
            const codepoints = Array.from(label)
            const formatRuns = bold
                ? [{ start: 0, end: codepoints.length, bold: true }]
                : null
            const lay = computeStringLayout(label, cache, cache.baseStep, formatRuns)
            return Math.max(1, Math.ceil((lay.totalWidth * scale) / cw))
        }

        let x = layout.innerX + 1
        for (const b of buttons) {
            // 3 Chars `[K]` + 1 Char Space + Label-Char-Breite (Bold-Variante
            // fuer alle hoverable Buttons, sonst Regular).
            const width = 4 + labelCharWidth(b.label, !b.disabled)
            if (x + width > layout.innerX + layout.innerWidth) break

            const isHover = !b.disabled && this._hoveredButtonKey === b.key
            if (isHover) {
                this._overlay.stripes.push({
                    x, y, width, bgStyle: Style.MENU_ITEM_SELECTED_BG
                })
            }

            const keyStyle = b.disabled ? Style.PANE_INFO
                          : isHover     ? Style.MENU_ITEM_KEY_SELECTED
                          : Style.MENU_ITEM_KEY
            const labelStyle = b.disabled ? Style.PANE_INFO
                            : isHover     ? Style.MENU_ITEM_SELECTED
                            : Style.MENU_ITEM
            this._overlay.setChar(x, y, '[', keyStyle)
            this._overlay.setChar(x + 1, y, b.key, keyStyle)
            this._overlay.setChar(x + 2, y, ']', keyStyle)
            this._overlay.addTextSpan(x + 4, y, b.label, labelStyle, b.label.length)
            // Hit-Test-Bounds in Button-Objekt cachen
            b._x = x
            b._y = y
            b._width = width
            // 2 Char Cells Inter-Button-Gap — mindestens 1 extra ueber das
            // Mindest-Padding, damit die Buttons im Prop-Mode (wo Label-Breite
            // genau auf eine Char-Cell aufgerundet werden kann) nicht
            // visuell aneinanderkleben.
            x += width + 2
        }
    }

    // ───────────────────────────────────────────────────────────────
    // Keyboard
    // ───────────────────────────────────────────────────────────────

    async handleKeydown(e) {
        if (!this.isOpen) return

        // Solange das Dashboard offen ist, gehoert das Tastatur-Event uns.
        // Sonst schlagen Cmd+Plus/Minus (Browser-Zoom), Cmd+R (Reload),
        // Cmd+W (Tab schliessen) etc. zum Browser durch und unterbrechen
        // die Bedienung. F-Keys (F5 Reload, F12 DevTools) bleiben dem
        // Browser ueberlassen — sind als Notausgang manchmal sinnvoll.
        const isFKey = typeof e.key === 'string' && /^F\d{1,2}$/.test(e.key)
        if (!isFKey) e.preventDefault()

        // PathPicker (Stufe D): solange offen, verschluckt er alle Tasten.
        // Liegt VOR der Edit-Pruefung, weil der Picker stets als Modal
        // ueber dem Dashboard liegt (und ein Edit unter dem Picker waere
        // ohnehin nicht erreichbar).
        if (this._pathPicker && this._pathPicker.isActive) {
            const handled = this._pathPicker.handleKey(e)
            if (handled) this._render()
            return
        }

        // Drag&Drop (Stufe E): ESC waehrend Drag cancelt sauber, kein Move.
        if ((this._dragState || this._dragCandidate) && e.key === 'Escape') {
            this._dragState = null
            this._dragCandidate = null
            this._render()
            return
        }

        // Context-Menu (Stufe G): konsumiert alle Tasten solange offen.
        if (this._contextMenu) {
            await this._handleContextMenuKey(e)
            return
        }

        // Suche (Stufe F): wenn die Search-Box den Fokus hat, gehoeren alle
        // Tasten ihr. Sonderfaelle:
        //   - ESC mit Text → Text leeren, Fokus bleibt (User kann weiter tippen)
        //   - ESC ohne Text → Dashboard schliessen (Spec §8.2)
        //   - Enter → erstes/aktuelles Resultat oeffnen
        //   - ↑/↓ → Treffer-Selektion
        if (this._searchActive) {
            await this._handleSearchKey(e)
            return
        }

        // `/`-Shortcut: aktiviert die Search-Box. Spec §12.1 + §8.1. Im
        // Trash-Modus ist Suche deaktiviert.
        if (e.key === '/' && !this._showTrash) {
            this._activateSearch()
            return
        }

        // Edit-Mode: SingleLineInput uebernimmt alle Eingaben. Escape =
        // cancel, Enter = commit (via onEnter-Callback). Alle Dashboard-
        // Shortcuts sind waehrend des Edits blockiert.
        if (this._editState) {
            if (e.key === 'Escape') { this._cancelEdit(); return }
            const handled = this._editState.input.handleKeydown(e)
            if (handled) this._render()
            return
        }

        // Cmd+Up: eine Folder-Ebene hoch (Spec §4.2 / §12.1). Muss VOR
        // dem regulaeren ArrowUp-Case stehen, sonst frisst der die
        // Modifier-Variante.
        if (e.key === 'ArrowUp' && (e.metaKey || e.ctrlKey)) {
            await this._navigateUp()
            return
        }

        switch (e.key) {
            case 'Escape':
                // Stufe J: im Kein-Projekt-Zustand blockiert (Spec §11.5).
                if (this._isNoProjectState()) {
                    this._pulseHint()
                    return
                }
                this.close()
                return

            case 'ArrowUp':
                if (this._selectedIndex > 0) this._selectedIndex--
                this._ensureSelectionVisible()
                this._render()
                return

            case 'ArrowDown':
                if (this._selectedIndex < this._currentList().length - 1) this._selectedIndex++
                this._ensureSelectionVisible()
                this._render()
                return

            case 'Enter':
                await this._runActionForKey(ACTIONS.OPEN)
                return

            case 'Tab':
                this._showTrash = !this._showTrash
                this._selectedIndex = 0
                this._scrollOffset = 0
                this._render()
                return

            case 'Delete':
            case 'Backspace':
                await this._runActionForKey(this._showTrash ? ACTIONS.PERM_DELETE : ACTIONS.DELETE)
                return
        }

        // Buchstaben-Shortcuts (ohne Modifier — Spec §12.1)
        if (!e.ctrlKey && !e.metaKey && !e.altKey && typeof e.key === 'string' && e.key.length === 1) {
            const key = e.key.toUpperCase()
            await this._runActionForKey(key)
        }
    }

    async _runActionForKey(key) {
        const buttons = this._layoutButtons || this._computeActionButtons()
        const btn = buttons.find(b => b.key === key && !b.disabled)
        if (!btn) {
            // Blockiert oder unbekannt — Ping (Spec §11.5 sinngemaess)
            this._ping()
            return
        }
        await this._executeAction(btn.action)
    }

    /**
     * Spielt den "verboten"-Ping. Zentrale Stelle, damit alle Pfade
     * (disabled-Button-Click, blockierte Shortcut-Taste, in Stufe J auch
     * Klick-im-Kein-Projekt-Zustand) das gleiche Verhalten haben.
     */
    _ping() {
        this._soundManager?.playPing?.()
    }

    async _executeAction(action) {
        switch (action) {
            case 'open':       return this._actionOpen()
            case 'closeTab':   return this._actionCloseTab()
            case 'rename':     return this._actionRename()
            case 'move':       return this._actionMove()
            case 'duplicate':  return this._actionDuplicate()
            case 'delete':     return this._actionDelete()
            case 'cloud':      return this._actionCloud()
            case 'newProject': return this._actionNewProject()
            case 'newFolder':  return this._actionNewFolder()
            case 'restore':    return this._actionRestore()
            case 'permDelete': return this._actionPermDelete()
            case 'emptyTrash': return this._actionEmptyTrash()
        }
    }

    // ───────────────────────────────────────────────────────────────
    // Actions
    // ───────────────────────────────────────────────────────────────

    /**
     * Liefert das selektierte Projekt — nur wenn der selektierte Eintrag
     * ein Projekt ist (bzw. im Trash-Modus immer, weil die Trash-Liste
     * nackte Projekt-Objekte enthaelt). Bei Folder/Parent-Selektion null.
     */
    _selectedProject() {
        if (this._showTrash) return this._trashProjects[this._selectedIndex] || null
        const entry = this._entries[this._selectedIndex]
        if (!entry || entry.type !== 'project') return null
        return entry.project
    }

    /** Folder-Objekt, wenn ein Folder selektiert ist, sonst null. */
    _selectedFolder() {
        if (this._showTrash) return null
        const entry = this._entries[this._selectedIndex]
        if (!entry || entry.type !== 'folder') return null
        return entry.folder
    }

    /** True wenn der Parent-Eintrag selektiert ist. */
    _isParentSelected() {
        if (this._showTrash) return false
        const entry = this._entries[this._selectedIndex]
        return !!entry && entry.type === 'parent'
    }

    async _actionOpen() {
        // Trash-Modus: kein Open. Spec §5.3 erlaubt nur Restore /
        // Permanently Delete / Empty Trash. Doppelklick und Enter wuerden
        // sonst geloeschte Projekte oeffnen, was den Sinn vom Trash bricht.
        if (this._showTrash) { this._ping(); return }
        if (this._isParentSelected()) {
            await this._navigateUp()
            return
        }
        const folder = this._selectedFolder()
        if (folder) {
            await this._navigateInto(folder.id)
            return
        }
        const project = this._selectedProject()
        if (!project) return
        this.close()
        this._onOpen?.(project.id)
    }

    /**
     * Springt in einen Folder hinein. Selektion auf den Parent-Eintrag (= 0)
     * stellen, damit der User sofort visuell sieht, wo er hingewechselt ist
     * und einfach wieder rauskommt.
     */
    async _navigateInto(folderId) {
        await this._setCurrentFolder(folderId)
        this._selectedIndex = 0
        this._scrollOffset = 0
        this._render()
    }

    /**
     * Direkter Sprung zu einem beliebigen Folder (per Breadcrumb-Klick).
     * No-op wenn wir schon da sind.
     * @param {string|null} folderId - null = Root
     */
    async _navigateToFolder(folderId) {
        const target = folderId || null
        if ((this._currentFolderId || null) === target) return
        await this._setCurrentFolder(target)
        this._selectedIndex = 0
        this._scrollOffset = 0
        this._render()
    }

    /**
     * Eine Folder-Ebene hoch. Selektion springt auf den Folder, aus dem
     * wir gerade kommen, damit der User den Kontext nicht verliert.
     */
    async _navigateUp() {
        if (!this._currentFolderId) { this._ping(); return }
        const cameFromId = this._currentFolderId
        const path = this._currentFolderPath
        const parentId = path.length >= 2 ? path[path.length - 2].id : null
        await this._setCurrentFolder(parentId)
        const idx = this._entries.findIndex(
            e => e.type === 'folder' && e.folder.id === cameFromId
        )
        this._selectedIndex = idx >= 0 ? idx : 0
        this._scrollOffset = 0
        this._render()
    }

    /**
     * Schliesst den Tab eines offenen Projekts (ohne das Projekt zu loeschen).
     * Dashboard bleibt offen — "Schreibtisch aufraeumen" als Workflow.
     * Wenn das aktive Projekt geschlossen wird: switch zu next Tab; gibt's
     * keinen → temporaeres "untitled" (Stufe-B-Pragma).
     *
     * **Stufe-J-Drift (offen)**: Spec §1.4/§11.5 sieht hier eigentlich den
     * Header-Hint-Zustand vor (Dashboard zeigt "Open or create a project").
     * Der Pragma bleibt, weil der Editor-Stack (switchToProject, currentProjectId-
     * Konsumenten) heute keine Null-Pfade hat — der volle Stufe-J-Move braucht
     * einen `currentProjectId === null`-faehigen Editor-Zustand. Wird als
     * eigene Phase migriert.
     */
    async _actionCloseTab() {
        const project = this._selectedProject()
        if (!project) return
        if (!this._tabManager?.isOpen?.(project.id)) return

        const wasActive = project.id === this._projectManager.currentProjectId
        if (wasActive) {
            await this._projectManager.flushNow()
        }
        let next = this._tabManager.closeTab(project.id)
        if (wasActive) {
            if (!next) {
                next = await this._projectManager.createProject('untitled')
                await this._autoCloudNewProject(next)
                this._tabManager?.openTab?.(next)
            }
            await this._onOpen?.(next)
        }
        await this._refresh()
        this._render()
    }

    /**
     * Rename auf Folder oder Projekt. Oeffnet die Inline-Edit-Zeile mit
     * dem aktuellen Namen vorbelegt. Folder-Rename triggert
     * FolderManager.renameFolder, Projekt-Rename ProjectManager.renameProject.
     */
    async _actionRename() {
        const project = this._selectedProject()
        if (project) {
            this._startEdit({
                mode: 'renameProject',
                target: project.id,
                initialText: project.title || ''
            })
            return
        }
        const folder = this._selectedFolder()
        if (folder && this._folderManager) {
            this._startEdit({
                mode: 'renameFolder',
                target: folder.id,
                initialText: folder.name || ''
            })
        }
    }

    /**
     * Move (Stufe D). Oeffnet den PathPicker mit allen Foldern; bei einem
     * Pick wird das Item an seinen Ziel-Folder verschoben.
     *
     * - Projekt-Move: `projectManager.moveProject(id, folderId|null)`.
     *   ProjectManager hat selbst einen No-Op-Guard, falls schon im Ziel-Folder.
     * - Folder-Move: `folderManager.moveFolder(id, folderId|null)` mit
     *   Zyklus-Schutz im Manager — der PathPicker filtert Self+Descendants
     *   zusaetzlich vorab aus der Auswahl raus (Spec §6.3 / §11.8).
     */
    async _actionMove() {
        const project = this._selectedProject()
        const folder = !project ? this._selectedFolder() : null
        if (!project && !folder) return
        if (folder && !this._folderManager) return

        const folders = this._folders.slice()
        const excludeIds = new Set()
        if (folder) {
            // Self + alle Descendants ausschliessen — sonst koennte der User
            // einen Folder in sich selbst oder unter einen seiner Sub-Folder
            // schieben (Zyklus).
            const descendants = await this._folderManager.getDescendantFolderIds(folder.id)
            for (const id of descendants) excludeIds.add(id)
        }

        const currentFolderId = project
            ? (project.folderId || null)
            : (folder.parentFolderId || null)
        const targetName = project
            ? (project.title || 'untitled')
            : (folder.name || 'Untitled')

        this._pathPicker.open({
            targetType: project ? 'project' : 'folder',
            targetId: project ? project.id : folder.id,
            targetName,
            currentFolderId,
            folders,
            excludeIds,
            onPick: async (folderId) => {
                await this._performMove({
                    targetType: project ? 'project' : 'folder',
                    targetId: project ? project.id : folder.id,
                    sourceFolderId: currentFolderId,
                    destFolderId: folderId || null
                })
            },
            onCancel: () => {
                // Picker hat sich bereits geschlossen — nur neu rendern,
                // damit das Modal sauber verschwindet.
                this._render()
            }
        })
        this._render()
    }

    /**
     * Fuehrt den eigentlichen Move aus (vom PathPicker-onPick gerufen).
     * Trennt die UI-Entscheidung (Picker) vom Daten-Mutation-Pfad, damit
     * Tests den Move-Pfad ohne Picker fahren koennen.
     */
    async _performMove({ targetType, targetId, sourceFolderId, destFolderId }) {
        const src = sourceFolderId || null
        const dst = destFolderId || null
        // No-Op wenn Quelle und Ziel identisch sind (User hat (current)
        // gepickt). Picker bereits geschlossen, nur re-render.
        if (src === dst) {
            this._render()
            return
        }

        if (targetType === 'project') {
            await this._projectManager.moveProject(targetId, dst)
        } else if (targetType === 'folder' && this._folderManager) {
            const result = await this._folderManager.moveFolder(targetId, dst)
            if (!result || result.ok === false) {
                // Defense-in-Depth: Picker hat den Zyklus vorab gefiltert,
                // aber falls der FolderManager doch ablehnt (z.B. weil
                // sich der Tree zwischen Filter und Pick veraendert hat),
                // freundlich melden statt stiller Fail.
                if (this._dialogManager) {
                    await this._withDialog(() => this._dialogManager.showInfo(
                        'Move failed',
                        result?.reason === 'cycle'
                            ? 'Cannot move a folder into itself or one of its sub-folders.'
                            : 'Move failed. Please try again.'
                    ))
                }
                this._render()
                return
            }
        }

        await this._refresh()
        // Selektion stabil halten: wenn das Item noch im sichtbaren Folder
        // liegt (Source==CurrentFolder oder Dest==CurrentFolder), `_refresh`
        // hat schon ueber prevKey nachjustiert. Wenn das Item den Folder
        // verlassen hat, faellt die Selektion auf 0 — sauber genug.
        this._render()
    }

    /**
     * Drag&Drop (Stufe E): Drop-Target am Cursor ermitteln. Liefert
     * `{ ok: true, folderId, ...extra }` fuer einen gueltigen Drop,
     * `{ ok: false, reason }` fuer einen ungueltigen (Zyklus, No-Op) und
     * `null` fuer "kein Target" (Hover ueber leeren Bereich / Project /
     * Frame-Rand). Spec §7.3 + §7.4.
     *
     * Drop-Targets:
     *   - Folder-Zeile in der Liste → in den Folder
     *   - Parent-Eintrag (`◁`) → eine Ebene hoch
     *   - Breadcrumb-Segment → in das Segment-Folder
     */
    _computeDropTarget(charX, charY) {
        const L = this._layout
        if (!L) return null

        // 1) Breadcrumb-Segment (Spec §7.3 dritter Bullet)
        const B = this._layoutBreadcrumb
        if (B && charY === B.y) {
            for (const seg of B.segments) {
                if (charX >= seg.x && charX < seg.x + seg.width) {
                    // Trash-Segmente sind kein gueltiger Drop-Target.
                    if (seg.id === '__trash' || seg.id === '__trashroot') return null
                    return this._validateDrop(seg.id || null, { kind: 'breadcrumb', segIndex: B.segments.indexOf(seg) })
                }
            }
        }

        // 2) Listen-Eintrag (Folder oder Parent)
        if (charX >= L.innerX && charX < L.innerX + L.innerWidth
                && charY >= L.listStartY && charY < L.listEndY) {
            const idx = (charY - L.listStartY) + this._scrollOffset
            const list = this._currentList()
            const entry = list[idx]
            if (!entry) return null
            if (entry.type === 'parent') {
                const path = this._currentFolderPath
                const parentId = path.length >= 2 ? path[path.length - 2].id : null
                return this._validateDrop(parentId, { kind: 'parent', rowIndex: idx })
            }
            if (entry.type === 'folder') {
                return this._validateDrop(entry.folder.id, { kind: 'folder', rowIndex: idx })
            }
            // Drop auf ein Project ist kein Target (Spec §7.3 vierter Bullet)
            return null
        }
        return null
    }

    /**
     * Validiert einen Drop gegen das aktuell laufende Drag:
     *   - No-Op: Source-Folder == Ziel-Folder → ungueltig
     *   - Zyklus: Folder-Drop auf sich selbst oder einen Descendant
     *     (Spec §7.4)
     * Liefert `{ ok, folderId, ...extra, reason? }`.
     */
    _validateDrop(targetFolderId, extra = {}) {
        const drag = this._dragState || this._dragCandidate
        if (!drag) return null
        const src = drag.sourceFolderId || null
        const dst = targetFolderId || null
        if (src === dst) {
            return { ok: false, folderId: targetFolderId, reason: 'noop', ...extra }
        }
        if (drag.type === 'folder') {
            if (targetFolderId === drag.id) {
                return { ok: false, folderId: targetFolderId, reason: 'cycle', ...extra }
            }
            if (this._isDescendantFolder(targetFolderId, drag.id)) {
                return { ok: false, folderId: targetFolderId, reason: 'cycle', ...extra }
            }
        }
        return { ok: true, folderId: targetFolderId, ...extra }
    }

    /**
     * Synchroner Descendant-Check ueber den lokalen Folder-Cache. Wird im
     * Drag-Hot-Path pro Mouse-Move gerufen, deshalb kein async — der
     * FolderManager-Variant wuerde IndexedDB anfassen.
     *
     * @returns {boolean} true wenn `candidateId` ein Descendant von
     *   `ancestorId` ist (oder identisch — wird in `_validateDrop` schon
     *   vorher abgefangen).
     */
    _isDescendantFolder(candidateId, ancestorId) {
        if (!candidateId || !ancestorId) return false
        let cur = candidateId
        const guard = new Set()
        while (cur && !guard.has(cur)) {
            guard.add(cur)
            if (cur === ancestorId) return true
            const f = this._folders.find(ff => ff.id === cur)
            if (!f) break
            cur = f.parentFolderId || null
        }
        return false
    }

    async _actionDuplicate() {
        const project = this._selectedProject()
        if (!project) return
        const newId = await this._projectManager.duplicateProject(project.id)
        // Cloud-first auch fuer Duplikate (2026-07-02, ersetzt Spec §5.1
        // "landet immer lokal"): ein Duplikat ist aus Nutzersicht ein neues
        // Projekt — der Batch-Upload in enableCloudForNewProject nimmt die
        // kopierten Paragraphen mit.
        await this._autoCloudNewProject(newId)
        await this._refresh()
        // Selektion auf das Duplikat (Folder-Modus: Entry-Match, Trash bleibt
        // theoretisch nie hier — Duplicate ist im Trash disabled).
        const idx = this._entries.findIndex(
            e => e.type === 'project' && e.project.id === newId
        )
        if (idx >= 0) this._selectedIndex = idx
        this._render()
    }

    async _actionDelete() {
        // Folder selektiert → Delete-All-Pfad (rekursiv, Spec §5.2).
        const folder = this._selectedFolder()
        if (folder) return this._actionDeleteFolder(folder)

        const project = this._selectedProject()
        if (!project) return
        const confirmed = await this._withDialog(() => this._dialogManager.showConfirm(
            'Delete project',
            `"${project.title || 'untitled'}" will be moved to the Deleted list. Continue?`
        ))
        if (!confirmed) return

        const wasActive = project.id === this._projectManager.currentProjectId
        const wasOpenTab = this._tabManager?.isOpen?.(project.id)

        // Reihenfolge ist kritisch: wenn wir das aktive Projekt loeschen,
        // muessen wir VORHER weg-switchen, sonst schreibt der naechste Flush
        // (im Editor-Hintergrund oder im switchToProject selbst) das `deletedAt`
        // wieder weg.
        //
        // 1. Pending Editing flushen — sichert die letzten Aenderungen,
        //    bevor wir das Projekt aus den Tabs nehmen.
        if (wasActive) {
            await this._projectManager.flushNow()
        }

        // 2. Tab schliessen + auf naechstes Projekt switchen, falls das aktive
        //    geloescht wird. Stufe-B-Pragma: wenn kein anderes Tab existiert,
        //    legen wir ein neues "untitled" an und switchen dorthin. Stufe J
        //    ersetzt das durch den Header-Hint "Open or create a project".
        let switchTarget = null
        if (wasOpenTab) {
            switchTarget = this._tabManager.closeTab(project.id)
        }
        if (wasActive) {
            if (!switchTarget) {
                switchTarget = await this._projectManager.createProject('untitled')
                await this._autoCloudNewProject(switchTarget)
                this._tabManager?.openTab?.(switchTarget)
            }
            await this._onOpen?.(switchTarget)
        }

        // 3. Erst jetzt Soft-Delete — _currentProject ist (falls wasActive)
        //    bereits umgestellt, und im RAM-Cache des nicht-mehr-aktiven
        //    Projekts setzt softDeleteProject `deletedAt` zusaetzlich, falls
        //    es noch gecached war (Defense-in-Depth).
        await this._projectManager.softDeleteProject(project.id)

        await this._refresh()
        this._clampSelection()
        this._render()
    }

    /**
     * Folder-Delete-All (Spec §5.2). Sammelt rekursiv alle Projekte und
     * Sub-Folder des Targets. Wenn ein Projekt im Unterbaum aktuell als
     * Tab offen ist, wird die Aktion blockiert (Spec §11.2). Sonst:
     * modale Rueckfrage mit den Counts, dann Soft-Delete der Projekte
     * (sie landen in der Deleted-Liste) und Hart-Delete der Folder.
     */
    async _actionDeleteFolder(folder) {
        if (!this._folderManager) return

        // Descendant-Folder (inkl. Target selbst). Die Tree-Logik lebt im
        // FolderManager — ein einziger getAll-Roundtrip + BFS.
        const descendantIds = await this._folderManager.getDescendantFolderIds(folder.id)
        const descendantSet = new Set(descendantIds)

        // Alle Projekte im Unterbaum. _projects ist bereits gefiltert auf
        // aktive (nicht-deleted) Projekte, also kein doppelter Filter noetig.
        const affected = this._projects.filter(
            p => p.folderId && descendantSet.has(p.folderId)
        )

        // Block-Check: ist eines der Projekte gerade als Tab offen?
        const openTabs = new Set(this._tabManager?.openTabs || [])
        const openInTree = affected.find(p => openTabs.has(p.id))
        if (openInTree) {
            await this._withDialog(() => this._dialogManager.showInfo(
                'Cannot delete folder',
                `Project "${openInTree.title || 'untitled'}" is currently open. Close it first.`
            ))
            return
        }

        const projCount = affected.length
        const subCount = descendantIds.length - 1  // minus den Target-Folder selbst
        const parts = []
        if (projCount > 0) {
            parts.push(
                `${projCount} project${projCount === 1 ? '' : 's'} will be moved to the Deleted list.`
            )
        }
        if (subCount > 0) {
            parts.push(
                `The folder structure (including ${subCount} sub-folder${subCount === 1 ? '' : 's'}) will be permanently removed.`
            )
        } else {
            parts.push('The folder will be permanently removed.')
        }
        const confirmed = await this._withDialog(() => this._dialogManager.showConfirm(
            'Delete folder',
            `Delete folder "${folder.name || 'Untitled'}"? ${parts.join(' ')}`
        ))
        if (!confirmed) return

        // Re-Check gegen Tab-Open zwischen Confirm-Dialog und Delete-Loop:
        // der Dialog kann mehrere Sekunden offen sein, in denen der User ein
        // Projekt im Unterbaum via Tab-Bar oeffnet. Ohne den Re-Check wuerde
        // der Delete trotzdem laufen und der Editor stuende mit einem zur
        // softDelete-Markierung divergenten lokalen State da.
        const openTabsNow = new Set(this._tabManager?.openTabs || [])
        const reopenedInTree = affected.find(p => openTabsNow.has(p.id))
        if (reopenedInTree) {
            await this._withDialog(() => this._dialogManager.showInfo(
                'Cannot delete folder',
                `Project "${reopenedInTree.title || 'untitled'}" was opened during the confirmation dialog. Close it first.`
            ))
            return
        }

        // Reihenfolge ist hier unkritisch: Projekte sind soft-delete (lokal +
        // Server via Sync-Tombstone), Folder sind hart-delete. Die FolderManager-
        // CASCADE-Logik liegt serverseitig (FK ON DELETE SET NULL fuer
        // projects.folder_id, plus eigene Folder-Cascade), lokal sind beide
        // Stores entkoppelt.
        for (const p of affected) {
            await this._projectManager.softDeleteProject(p.id)
        }
        for (const fid of descendantIds) {
            await this._folderManager.deleteFolder(fid)
        }

        await this._refresh()
        this._clampSelection()
        this._render()
    }

    async _actionCloud() {
        const project = this._selectedProject()
        if (!project || !this._syncService) return
        // Wir delegieren an die bestehende Cloud-Toggle-Logik (cloudToggleLegacy).
        // Dort kann ein Confirm-Dialog erscheinen (Cloud→Lokal) oder ein
        // Info-Dialog bei Fehlern — beides muss durch _withDialog gehen,
        // damit Pane-Borders sauber zurueckkommen.
        if (this._onCloudToggle) {
            await this._withDialog(() => this._onCloudToggle(project))
            await this._refresh()
        }
    }

    setOnCloudToggle(fn) { this._onCloudToggle = fn }

    /**
     * Neue Projekte sind cloud-first (Testphasen-Entscheidung 2026-07-02):
     * nach dem Anlegen still Cloud-Sync aktivieren. Schlaegt das fehl
     * (offline, Server down), bleibt das Projekt lokal — die Cloud-Spalte
     * zeigt dann "local" und der User kann manuell aktivieren.
     */
    async _autoCloudNewProject(projectId) {
        if (!this._syncService) return
        try {
            const project = await this._projectManager.getProject(projectId)
            if (!project) return
            await enableCloudForNewProject({
                project,
                projectManager: this._projectManager,
                syncService: this._syncService
            })
        } catch (err) {
            console.warn('Auto-Cloud fuer neues Projekt fehlgeschlagen:', err.message)
        }
    }

    async _actionNewProject() {
        // Spec §5.3: New Project landet immer im aktuellen Folder.
        // Symmetrisch zu newFolder: Inline-Edit fuer den Namen, leerer
        // Name → Auto-Default "Untitled" (Spec §11.7).
        this._startEdit({ mode: 'newProject', target: null, initialText: '' })
    }

    /**
     * Legt einen neuen Folder im aktuellen Folder an. Oeffnet die Inline-
     * Edit-Zeile (oben in der Liste, nach dem Parent-Eintrag falls
     * vorhanden). Leerer Name → Auto-Default "Untitled" (Spec §11.7).
     */
    async _actionNewFolder() {
        if (!this._folderManager) return
        this._startEdit({ mode: 'newFolder', target: null, initialText: '' })
    }

    // ───────────────────────────────────────────────────────────────
    // Inline-Edit (Stufe C-3)
    // ───────────────────────────────────────────────────────────────

    /**
     * Startet den Inline-Edit. Erzeugt eine SingleLineInput-Instanz und
     * stellt sicher, dass die Edit-Zeile selektiert ist (damit die
     * Render-Logik die Position kennt).
     * @param {{mode: 'newFolder'|'renameFolder'|'renameProject', target: string|null, initialText: string}} args
     */
    _startEdit({ mode, target, initialText }) {
        const input = new SingleLineInput({
            onEnter: () => { this._commitEdit() }
        })
        input.setText(initialText || '')
        // Komplette Vorbelegung selektieren — klassische "Rename"-UX, ein
        // Tastendruck ersetzt den ganzen Namen.
        input.selectionStart = 0
        input.selectionEnd = input.text.length
        input.cursorPos = input.text.length
        this._editState = { mode, target, input }
        // Selektion auf die Edit-Zeile setzen (visible-list Index).
        const visible = this._visibleEntries()
        let idx = -1
        if (mode === 'newFolder' || mode === 'newProject') {
            idx = this._currentFolderId ? 1 : 0
        } else if (mode === 'renameFolder') {
            idx = visible.findIndex(e => e.type === 'editFolder' && e.folder?.id === target)
        } else if (mode === 'renameProject') {
            idx = visible.findIndex(e => e.type === 'editProject' && e.project?.id === target)
        }
        if (idx >= 0) this._selectedIndex = idx
        this._render()
        this._ensureSelectionVisible()
        this._render()
    }

    /**
     * Bricht den Inline-Edit ab — kein Schreibvorgang. Selektion bleibt
     * auf der urspruenglichen Zeile (oder springt beim newFolder auf den
     * Parent-Eintrag bzw. Index 0).
     */
    _cancelEdit() {
        if (!this._editState) return
        const wasNew = this._editState.mode === 'newFolder' || this._editState.mode === 'newProject'
        this._editState = null
        if (wasNew) {
            this._selectedIndex = this._currentFolderId ? 0 : 0  // Parent-Eintrag oder Top
        }
        this._render()
    }

    /**
     * Schliesst den Inline-Edit ab. Auto-Default "Untitled" bei leerem
     * Name (Spec §11.7), Duplikat-Check (Spec §11.6) mit modalem Dialog.
     */
    async _commitEdit() {
        if (!this._editState) return
        const { mode, target, input } = this._editState
        const name = (input.getText() || '').trim() || 'Untitled'

        const ok = await this._checkDuplicateName(mode, target, name)
        if (!ok) {
            // User hat im Dialog "No" gewaehlt — Edit bleibt offen, Cursor
            // ans Ende setzen damit weiter getippt werden kann.
            if (this._editState) {
                const inp = this._editState.input
                inp.selectionStart = null
                inp.selectionEnd = null
                inp.cursorPos = inp.text.length
                this._render()
            }
            return
        }

        if (mode === 'newFolder') {
            const folder = await this._folderManager.createFolder(name, this._currentFolderId || null)
            this._editState = null
            await this._refresh()
            const idx = this._entries.findIndex(
                e => e.type === 'folder' && e.folder.id === folder.id
            )
            if (idx >= 0) this._selectedIndex = idx
        } else if (mode === 'newProject') {
            const id = await this._projectManager.createProject(name, '', this._currentFolderId || null)
            await this._autoCloudNewProject(id)
            this._editState = null
            await this._refresh()
            const idx = this._entries.findIndex(
                e => e.type === 'project' && e.project.id === id
            )
            if (idx >= 0) this._selectedIndex = idx
        } else if (mode === 'renameFolder') {
            await this._folderManager.renameFolder(target, name)
            this._editState = null
            await this._refresh()
        } else if (mode === 'renameProject') {
            await this._projectManager.renameProject(target, name)
            this._editState = null
            await this._refresh()
        }
        this._render()
    }

    /**
     * Duplikat-Check fuer Spec §11.6: gleicher Name im selben Folder.
     * Folder vs. Projekt im selben Folder sind erlaubt (kein Konflikt).
     * Bei Konflikt: modale Rueckfrage. Returnt true wenn Commit erlaubt.
     */
    async _checkDuplicateName(mode, target, name) {
        if (mode === 'newFolder' || mode === 'renameFolder') {
            const parent = this._currentFolderId || null
            const dup = this._folders.some(f =>
                (f.parentFolderId || null) === parent
                && f.id !== target
                && f.name === name
            )
            if (!dup) return true
            return this._withDialog(() => this._dialogManager.showConfirm(
                'Duplicate folder name',
                `A folder named "${name}" already exists in this folder. Continue?`
            ))
        }
        if (mode === 'renameProject' || mode === 'newProject') {
            const folderId = mode === 'renameProject'
                ? (this._projects.find(p => p.id === target)?.folderId || null)
                : (this._currentFolderId || null)
            const dup = this._projects.some(p =>
                (p.folderId || null) === folderId
                && p.id !== target  // bei newProject ist target null → matcht alle
                && (p.title || '') === name
            )
            if (!dup) return true
            return this._withDialog(() => this._dialogManager.showConfirm(
                'Duplicate project name',
                `A project named "${name}" already exists in this folder. Continue?`
            ))
        }
        return true
    }

    async _actionRestore() {
        const project = this._selectedProject()
        if (!project) return
        const originalFolderId = project.folderId || null
        await this._projectManager.restoreProject(project.id)
        // Spec §5.2: Original-Folder gewinnt, wenn er noch existiert —
        // sonst Root. Sonst verschwindet das Projekt im "Nirwana": es hat
        // eine folderId, die zu keinem aktuellen Folder gehoert, taucht
        // weder im Root noch in einem sichtbaren Folder auf.
        if (originalFolderId && !this._folders.find(f => f.id === originalFolderId)) {
            await this._projectManager.moveProject(project.id, null)
        }
        await this._refresh()
        this._clampSelection()
        this._render()
    }

    async _actionPermDelete() {
        const project = this._selectedProject()
        if (!project) return
        const confirmed = await this._withDialog(() => this._dialogManager.showConfirm(
            'Delete permanently',
            `"${project.title || 'untitled'}" will be permanently deleted. Continue?`
        ))
        if (!confirmed) return
        if (this._tabManager?.isOpen?.(project.id)) {
            const next = this._tabManager.closeTab(project.id)
            if (project.id === this._projectManager.currentProjectId && next) {
                this._onOpen?.(next)
            }
        }
        await this._projectManager.permanentDeleteProject(project.id)
        await this._refresh()
        this._clampSelection()
        this._render()
    }

    async _actionEmptyTrash() {
        const count = this._trashProjects.length
        if (count === 0) return
        const confirmed = await this._withDialog(() => this._dialogManager.showConfirm(
            'Empty deleted',
            `${count} project${count === 1 ? '' : 's'} will be permanently deleted. Continue?`
        ))
        if (!confirmed) return
        for (const p of [...this._trashProjects]) {
            if (this._tabManager?.isOpen?.(p.id)) {
                const next = this._tabManager.closeTab(p.id)
                if (p.id === this._projectManager.currentProjectId && next) {
                    this._onOpen?.(next)
                }
            }
            await this._projectManager.permanentDeleteProject(p.id)
        }
        await this._refresh()
        this._selectedIndex = 0
        this._render()
    }

    _clampSelection() {
        const list = this._currentList()
        if (list.length === 0) this._selectedIndex = 0
        else this._selectedIndex = Math.min(this._selectedIndex, list.length - 1)
    }

    /**
     * Stellt sicher, dass der selektierte Eintrag im sichtbaren Viewport ist —
     * wird NUR von Tastatur-Navigation (ArrowUp/Down) gerufen. Wheel-Scroll
     * laesst die Selektion bewusst aus dem Sichtbereich rutschen.
     */
    _ensureSelectionVisible() {
        if (!this._layout) return
        const visible = this._layout.listVisible
        if (visible <= 0) return
        if (this._selectedIndex < this._scrollOffset) {
            this._scrollOffset = this._selectedIndex
        } else if (this._selectedIndex >= this._scrollOffset + visible) {
            this._scrollOffset = this._selectedIndex - visible + 1
        }
    }

    /**
     * Re-rendert das Dashboard nach Dialog-Aufrufen. Mit dem Overlay-Stack
     * (Base + Top) braucht es kein Deaktivieren mehr — der Dialog lebt auf
     * dem Top-Layer und ueberlagert das Dashboard. Nach dem Dialog rendern
     * wir trotzdem neu, damit Inhalte (z.B. Cloud-Status) frisch sind.
     */
    async _withDialog(fn) {
        try {
            return await fn()
        } finally {
            if (this.isOpen) this._render()
        }
    }

    // ───────────────────────────────────────────────────────────────
    // Mouse
    // ───────────────────────────────────────────────────────────────

    /**
     * Mouse-Down (Stufe E Drag&Drop). Captured ein Drag-Candidate fuer einen
     * Folder oder Projekt-Eintrag. Erst wenn handleMouseMove eine Cell-
     * Bewegung registriert, wird daraus ein echter `_dragState`. Pre-Drag
     * ohne Bewegung wird beim mouseup verworfen und der nachfolgende
     * click-Event laeuft wie sonst (Selektion / Open).
     *
     * @returns {boolean} true wenn ein Drag-Candidate registriert wurde
     */
    handleMouseDown(charX, charY) {
        if (!this.isOpen || !this._layout) return false
        // Modal-Layer hat Prioritaet — beim Picker keine Drag-Aufnahme.
        if (this._pathPicker && this._pathPicker.isActive) return false
        // Context-Menu ist modal — Linksklick darunter triggert kein Drag,
        // der nachfolgende click schliesst das Menu (handleClick-Pfad).
        if (this._contextMenu) return false
        // Inline-Edit blockiert Drag (User soll erst committen/abbrechen).
        if (this._editState) return false
        // Trash hat keine Folder und kein Move (Spec §5.3) — kein Drag.
        if (this._showTrash) return false
        // Such-Modus hat eigene Click-Semantik (Treffer-Navigation) und
        // unterstuetzt kein Drag — Source-Folder ist nicht der currentFolder.
        if (this._searchText) return false

        const L = this._layout
        if (charX < L.innerX || charX >= L.innerX + L.innerWidth) return false
        if (charY < L.listStartY || charY >= L.listEndY) return false

        const idx = (charY - L.listStartY) + this._scrollOffset
        const list = this._currentList()
        const entry = list[idx]
        if (!entry) return false
        if (entry.type !== 'folder' && entry.type !== 'project') return false

        const id = entry.type === 'folder' ? entry.folder.id : entry.project.id
        const name = entry.type === 'folder'
            ? (entry.folder.name || 'Untitled')
            : (entry.project.title || 'untitled')
        const sourceFolderId = entry.type === 'folder'
            ? (entry.folder.parentFolderId || null)
            : (entry.project.folderId || null)

        this._dragCandidate = {
            type: entry.type, id, name, sourceFolderId,
            sourceIndex: idx,
            startX: charX, startY: charY
        }
        return true
    }

    /**
     * Mouse-Up (Stufe E Drag&Drop). Wenn ein Drag laeuft: Drop ausfuehren
     * (oder cancel bei invalidem Target). Setzt `_justFinishedDrag`, damit
     * der nachfolgende click-Event vom Browser nicht zusaetzlich die
     * Selektion auf die Drop-Position verschiebt.
     *
     * @returns {Promise<boolean>} true wenn ein Drag-Lifecycle abgeschlossen wurde
     */
    async handleMouseUp(charX, charY) {
        if (this._dragState) {
            // Drop-Target neu berechnen — der Cursor kann sich zwischen
            // letztem move und mouseup leicht bewegt haben.
            const target = this._computeDropTarget(charX, charY) || this._dragState.dropTarget
            const dragSnapshot = this._dragState
            this._dragState = null
            this._dragCandidate = null
            this._justFinishedDrag = true
            if (target && target.ok) {
                await this._performMove({
                    targetType: dragSnapshot.type,
                    targetId: dragSnapshot.id,
                    sourceFolderId: dragSnapshot.sourceFolderId,
                    destFolderId: target.folderId
                })
            } else {
                // Invalides Target, No-Op oder gar kein Target → haptisches
                // Feedback per Ping, damit der User merkt: "ja, war ein Drop,
                // aber nichts hat sich bewegt" (Findings aus Stufe E).
                this._ping()
                this._render()
            }
            return true
        }
        if (this._dragCandidate) {
            // mousedown ohne Bewegung — pre-Drag verwerfen, der gleich
            // folgende click-Event handhabt die Selektion wie sonst.
            this._dragCandidate = null
        }
        return false
    }

    handleMouseMove(charX, charY) {
        if (!this.isOpen || !this._layout) return false

        // Context-Menu (Stufe G): Hover updated den selektierten Eintrag.
        if (this._contextMenu) {
            const cm = this._contextMenu
            // 1 Cell Padding fuer Frame oben/unten, Items zwischen y+1..y+1+n.
            if (charX >= cm.x && charX < cm.x + cm.width
                    && charY >= cm.y + 1 && charY < cm.y + 1 + cm.items.length) {
                const idx = charY - (cm.y + 1)
                if (idx !== cm.hoveredIndex) {
                    cm.hoveredIndex = idx
                    this._render()
                    return true
                }
            }
            return false
        }

        // PathPicker (Stufe D): konsumiert Mouse-Move ueber dem Modal,
        // sodass der darunterliegende Dashboard-Hover nicht durchschlaegt.
        if (this._pathPicker && this._pathPicker.isActive) {
            const handled = this._pathPicker.handleMouseMove(charX, charY)
            if (handled) { this._render(); return true }
            return false
        }

        // Drag-Trigger (Stufe E): erst wenn der Cursor seit mousedown ueber
        // eine Cell-Grenze gewandert ist, wird aus dem _dragCandidate ein
        // echter _dragState. Pixel-genauer 4-Pixel-Threshold der Spec ist
        // im Char-Grid mit Cell-Granularitaet hinreichend approximiert —
        // Mikro-Wackeln innerhalb derselben Cell triggert nichts.
        if (this._dragCandidate && !this._dragState) {
            if (charX !== this._dragCandidate.startX
                    || charY !== this._dragCandidate.startY) {
                this._dragState = {
                    ...this._dragCandidate,
                    x: charX, y: charY,
                    dropTarget: this._computeDropTarget(charX, charY)
                }
                this._render()
                return true
            }
        }
        if (this._dragState) {
            this._dragState.x = charX
            this._dragState.y = charY
            this._dragState.dropTarget = this._computeDropTarget(charX, charY)
            this._render()
            return true
        }

        const L = this._layout

        // Search-Mode (Stufe F): eigene Hover-Logik auf den Treffern.
        if (this._searchText) {
            const ridx = this._searchRowAt(charY)
            if (ridx !== this._searchHoveredIndex) {
                this._searchHoveredIndex = ridx
                this._render()
                return true
            }
            return false
        }

        // 1) Listen-Hover
        let newRowHover = -1
        if (charX >= L.innerX && charX < L.innerX + L.innerWidth
                && charY >= L.listStartY && charY < L.listEndY) {
            const idx = (charY - L.listStartY) + this._scrollOffset
            const list = this._currentList()
            if (idx >= 0 && idx < list.length) newRowHover = idx
        }

        // 2) Action-Bar-Hover (disabled Buttons sind nicht hoverable,
        //    analog drawMenuItem-Konvention).
        let newButtonHover = null
        for (const b of (this._layoutButtons || [])) {
            if (b._x !== undefined && !b.disabled
                    && charY === b._y && charX >= b._x && charX < b._x + b._width) {
                newButtonHover = b.key
                break
            }
        }

        const changed = (newRowHover !== this._hoveredIndex)
                     || (newButtonHover !== this._hoveredButtonKey)
        if (changed) {
            this._hoveredIndex = newRowHover
            this._hoveredButtonKey = newButtonHover
            this._render()
            return true
        }
        return false
    }

    async handleClick(charX, charY, { doubleClick = false } = {}) {
        if (!this.isOpen || !this._layout) return

        // Context-Menu (Stufe G): konsumiert alle Klicks. Klick auf ein
        // Item fuehrt aus, Klick ausserhalb schliesst — beides verschluckt
        // den Click, damit das darunterliegende Dashboard nicht reagiert.
        if (this._contextMenu) {
            const cm = this._contextMenu
            const insideItems = charX >= cm.x && charX < cm.x + cm.width
                && charY >= cm.y + 1 && charY < cm.y + 1 + cm.items.length
            if (insideItems) {
                const idx = charY - (cm.y + 1)
                const item = cm.items[idx]
                if (item) await this._invokeContextMenuItem(item)
            } else {
                this._closeContextMenu()
            }
            return
        }

        // Drag&Drop (Stufe E): nach einem Drag schickt der Browser noch einen
        // click hinterher. Den verschlucken, sonst springt die Selektion auf
        // die Drop-Position um (oder oeffnet bei Doppelklick versehentlich).
        if (this._justFinishedDrag) {
            this._justFinishedDrag = false
            return
        }

        // PathPicker (Stufe D): konsumiert ALLE Klicks (auch ausserhalb des
        // Modal-Frames — sonst wuerde ein Klick neben dem Picker das
        // Dashboard schliessen). Picker entscheidet selbst, ob er cancelt
        // oder pickt.
        if (this._pathPicker && this._pathPicker.isActive) {
            const handled = this._pathPicker.handleClick(charX, charY)
            if (handled) this._render()
            return
        }

        const L = this._layout

        // Klick ausserhalb des Frames → Dashboard schliessen (Spec §13).
        // Stufe J: Bei Kein-Projekt-Zustand blockieren → Ping + Hint-Pulse
        // (Spec §11.5).
        if (charX < L.x || charX >= L.x + L.width || charY < L.y || charY >= L.y + L.height) {
            if (this._isNoProjectState()) {
                this._pulseHint()
                return
            }
            // Wenn ein Edit laeuft: erst abbrechen (sonst geht der Input
            // mit dem Dashboard verloren). Cancel ist sicherer als Commit
            // bei einem Klick ins Leere — Boris kann jederzeit per Enter
            // explizit bestaetigen.
            if (this._editState) this._cancelEdit()
            this.close()
            return
        }

        // Im Edit-State: interne Klicks (Tabs, Breadcrumb, Header,
        // Action-Bar, Listenzeilen) sind blockiert. User muss Enter
        // druecken (commit) oder Escape (cancel) — jeder andere Klick
        // pingt als haptisches Feedback, dass das gerade nicht geht.
        if (this._editState) { this._ping(); return }

        // Search-Box-Klick (Stufe F): aktiviert die Box. Wenn schon aktiv,
        // bleibt es so — Klick auf die Box ist "Fokus setzen".
        const S = this._layoutSearch
        if (S && charY === S.y && charX >= S.x && charX < S.x + S.width) {
            this._activateSearch()
            return
        }

        // Klick ausserhalb der Search-Box (aber innerhalb des Dashboard-
        // Frames) → Fokus aus der Box raus, Text bleibt erhalten.
        if (this._searchActive) {
            this._deactivateSearch()
            // Klick faellt durch, damit der nachfolgende Pfad das eigentliche
            // Click-Target (Tab, Folder, Action-Button) behandelt.
        }

        // Such-Treffer-Klick (Stufe F): im Such-Modus sitzt eine eigene
        // Liste in der Listfache. Doppelklick = Open, Single-Klick =
        // Folder navigieren + Item selektieren (Spec §8.2).
        if (this._searchText && charX >= L.innerX && charX < L.innerX + L.innerWidth
                && charY >= L.listStartY && charY < L.listEndY) {
            const idx = this._searchRowAt(charY)
            if (idx >= 0 && idx < this._searchResults.length) {
                const now = Date.now()
                const DBL_MS = 350
                const isDouble = doubleClick
                    || (this._lastSearchClickIdx === idx
                        && (now - (this._lastSearchClickAt || 0)) < DBL_MS)
                this._lastSearchClickIdx = idx
                this._lastSearchClickAt = now
                if (isDouble) {
                    await this._openSearchResult(idx)
                } else {
                    await this._focusSearchResult(idx)
                }
                return
            }
            // Klick in der Listfache, aber neben einem Treffer → nichts
        }

        // Breadcrumb-Segment-Klick → direkter Sprung in das Segment-Folder
        // (Spec §4.2). Im Deleted-Modus ist "My Projects" der Rueckweg zu
        // den aktiven Projekten; "Deleted" selbst ist no-op (= aktuelle Ansicht).
        // Im Such-Modus ist "My Projects" der Rueckweg zur Folder-Ansicht
        // (loescht die Suche), "Search results" selbst ist no-op.
        const B = this._layoutBreadcrumb
        if (B && charY === B.y) {
            for (const seg of B.segments) {
                if (charX >= seg.x && charX < seg.x + seg.width) {
                    if (seg.id === '__trash' || seg.id === '__search') return  // schon hier
                    if (seg.id === '__searchroot') {
                        this._clearSearch()
                        this._render()
                        return
                    }
                    if (seg.id === '__trashroot') {
                        // Zurueck zur Active-Ansicht. Selektion wieder auf
                        // das aktive Projekt (oder Index 0) wie beim Open.
                        this._showTrash = false
                        this._scrollOffset = 0
                        this._selectedIndex = this._findSelectedDefault()
                        this._render()
                        return
                    }
                    await this._navigateToFolder(seg.id)
                    return
                }
            }
        }

        // Toggle-Tabs
        const T = this._layoutTabs
        if (T && charY === T.y) {
            if (charX >= T.projectsX && charX < T.projectsX + T.projectsWidth) {
                if (this._showTrash) { this._showTrash = false; this._selectedIndex = 0; this._scrollOffset = 0; this._render() }
                return
            }
            if (charX >= T.trashX && charX < T.trashX + T.trashWidth) {
                if (!this._showTrash) { this._showTrash = true; this._selectedIndex = 0; this._scrollOffset = 0; this._render() }
                return
            }
        }

        // Column-Header → Sort
        if (charY === L.colHeaderY) {
            if (charX >= L.colNameX && charX < L.colNameX + L.colNameWidth)         return this._cycleSort('name')
            if (charX >= L.colSizeX && charX < L.colSizeX + COL_SIZE)               return this._cycleSort('size')
            if (charX >= L.colModifiedX && charX < L.colModifiedX + COL_DATE)       return this._cycleSort('updated')
            if (charX >= L.colCreatedX && charX < L.colCreatedX + COL_DATE)         return this._cycleSort('created')
            // Cloud-Spalte ist nicht sortierbar — kein Klick-Effekt
            return
        }

        // Action-Bar-Buttons
        for (const b of (this._layoutButtons || [])) {
            if (b._x !== undefined && charY === b._y && charX >= b._x && charX < b._x + b._width) {
                if (b.disabled) { this._ping(); return }
                return this._executeAction(b.action)
            }
        }

        // Listen-Klick → Selektion. Doppelklick (zweiter Klick auf dasselbe
        // Item innerhalb DBL_MS) → Open. Browser-natives `dblclick` waere
        // sauberer, aber unser mouseHandlers-Pipeline dispatcht nur einzelne
        // Klicks — wir tracken die Doppelklick-Erkennung selbst.
        if (charX >= L.innerX && charX < L.innerX + L.innerWidth
                && charY >= L.listStartY && charY < L.listEndY) {
            const idx = (charY - L.listStartY) + this._scrollOffset
            const list = this._currentList()
            if (idx >= 0 && idx < list.length) {
                const now = Date.now()
                const DBL_MS = 350
                const isDouble = doubleClick
                    || (this._lastClickIdx === idx
                        && (now - (this._lastClickAt || 0)) < DBL_MS)
                this._lastClickIdx = idx
                this._lastClickAt = now
                this._selectedIndex = idx
                if (isDouble) return this._actionOpen()
                this._render()
                return
            }
        }
    }

    /**
     * Touch-Swipe-Scroll (Spec touch-optimization §6 Phase 4): wandelt Pixel-
     * Delta in Zeilen-Delta um (per charHeight) und reicht es an handleWheel
     * weiter, damit PathPicker/Search/Trash-Branches automatisch mit gehen.
     * deltaPx > 0: Finger nach oben gewischt → Inhalt nach oben → forward scroll.
     * @param {number} deltaPx
     * @returns {boolean} hat-gescrollt-Indikator
     */
    scrollByPixels(deltaPx) {
        if (!this.isOpen || !this._renderer || !this._renderer.charHeight) return false
        const deltaLines = deltaPx / this._renderer.charHeight
        return this.handleWheel(deltaLines)
    }

    /**
     * Mausrad-Handling — analog LinkPicker mit Wheel-Accumulator (Spec §4.4).
     * Selektion bleibt unveraendert, nur der Scroll-Offset wandert.
     * @param {number} deltaLines - fraktionale Zeilen-Delta
     */
    handleWheel(deltaLines) {
        if (!this.isOpen || !this._layout) return false

        // Context-Menu (Stufe G): solange offen, Wheel verschlucken — die
        // Liste darunter darf nicht wegscrollen, sonst landet das Menu
        // visuell auf einem anderen Eintrag als anchored.
        if (this._contextMenu) return true

        // PathPicker (Stufe D): eigener Wheel-Accumulator im Modal, dem
        // Picker-Layer gehoert das Scrolling.
        if (this._pathPicker && this._pathPicker.isActive) {
            const changed = this._pathPicker.handleWheel(deltaLines)
            if (changed) this._render()
            return true
        }

        // Search-Mode (Stufe F): eigener Wheel-Accumulator, scrollt die
        // Treffer-Liste (2 Cell-Zeilen pro Item, eigener Offset).
        if (this._searchText) {
            const visibleRows = Math.floor(this._layout.listVisible / 2)
            const maxScroll = Math.max(0, this._searchResults.length - visibleRows)
            if (maxScroll === 0) return false
            this._searchWheelAccumulator += deltaLines
            const step = Math.trunc(this._searchWheelAccumulator)
            if (step === 0) return false
            this._searchWheelAccumulator -= step
            const next = Math.max(0, Math.min(this._searchScrollOffset + step, maxScroll))
            if (next === this._searchScrollOffset) return false
            this._searchScrollOffset = next
            this._render()
            return true
        }

        const list = this._currentList()
        const visible = this._layout.listVisible
        const maxScroll = Math.max(0, list.length - visible)
        if (maxScroll === 0) return false

        this._wheelAccumulator += deltaLines
        const step = Math.trunc(this._wheelAccumulator)
        if (step === 0) return false
        this._wheelAccumulator -= step
        const next = Math.max(0, Math.min(this._scrollOffset + step, maxScroll))
        if (next === this._scrollOffset) return false
        this._scrollOffset = next
        this._render()
        return true
    }
}
